import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Spinner } from 'reactstrap';
import { Link, useHistory, useLocation } from 'react-router-dom';
import cs_analysis_fetch from '../../utils/cs_analysis_fetch';
import {
    EMPTY_RETRIEVALS_HINT, UNANSWERED_PATH, UNITS_PATH, code_suggestion_body, code_suggestion_label, code_suggestion_note, code_suggestion_paths,
    code_suggestion_tone, empty_gaps_text, empty_retrievals_label, filter_gaps, gap_answered, gap_dom_id, gap_form_invalid, gap_form_seed_from_suggestion,
    gap_meta_line, gap_receipt_text, gap_summary_line, gap_unit_body, gaps_total, normalize_unanswered, parse_triggers, phrasings_label,
} from './knowledge_gaps';
import {
    REP_EMPTY, REP_FOOTER, REP_INTRO, REP_KINDS, REP_QUEUE_PATH, REP_TEXT_MAX, REP_VERDICTS, demand_text, expiry_text, normalize_rep_answer, normalize_rep_queue,
    rep_answer_body, rep_answer_path, rep_dom_id, rep_open_count, rep_receipt, rep_undo_path, text_sent_with, unrouted_text, wrong_label,
} from './knowledge_rep_queue';

/**
 * Knowledge — the reps' actionables on the knowledge base, six tabs.
 * Knowledge promotes itself (Nikhil 2026-09-13/14): the automation lifts
 * what the code confirms or two clients corroborate to the rep tier, and a
 * code-consistent unit that passes the safety screen goes client-facing on
 * its own. The reps' part is the human check on that, not an approval queue.
 *
 *   Rep review (default)    GET /api/v1/knowledge/rep-queue — Nikhil
 *     2026-10-22: "yes to the rep queue". One short queue, up to 10 checks a
 *     day ranked by how many clients ask, five kinds only (a rep-vs-knowledge
 *     case no product check can settle, a unit the safety screen was unsure
 *     about, a check that changed a client-served answer, a unit two client
 *     users marked unhelpful, the weekly sample of what reached clients
 *     without a rep). One tap: Right / Wrong / Not sure, plus an optional
 *     corrected text (POST …/rep-queue/:id/answer; a changed answer's Wrong is
 *     its one-tap undo, POST …/rep-queue/:id/undo). Skipped items keep the
 *     safe default and drop off after 7 days. Helpers: knowledge_rep_queue.js.
 *   Review queue            GET /api/v1/knowledge/client-review — a fixed
 *     weekly sample of what went client-safe automatically, the units the
 *     safety screen was unsure about, and the units demoted in the last 30
 *     days (with why). Actions are the existing unit signals — POST
 *     …/units/:id/attest (Looks right → REP_ATTESTED, client-facing) and POST
 *     …/units/:id/flag {reason} (Wrong or outdated → an auto-attested unit is
 *     demoted that night). Same routes as the Chat thread's ✓ / ✗.
 *   Answer contradictions   GET /api/v1/knowledge/answer-contradictions — the
 *     triage of cases where the knowledge base's answer to a real client
 *     question disagreed with what the rep said; a rep's verdict (POST
 *     …/answer-contradictions/:caseId/verdict — KB_RIGHT · REP_RIGHT · BOTH ·
 *     UNCLEAR, optional note) confirms or flags the cited units and can turn
 *     the rep's answer into a new unit. The page's module filter narrows it
 *     client-side.
 *   Code contradictions     GET /api/v1/knowledge/contradictions — proposals
 *     the code judge rejected because the product code says otherwise, with
 *     its note and the code-derived facts it checked against (`grounding`);
 *     copied as markdown for the product handover — clipboard only.
 *   Proposals               GET /api/v1/knowledge/proposals — what the
 *     conversation miner extracted overnight. Approving here is optional.
 *     Approve as-is or after an inline edit (POST …/units/:id/approve sends
 *     only the fields that changed; the server may merge it into an existing
 *     fact), or reject with an optional reason (POST …/units/:id/reject →
 *     RETIRED). Review signals on a row (all optional): impact +
 *     impactReasons[], consistency + consistencyNote / Confidence /
 *     CheckedAt, refinesUnitId + refinesTitle. Sort (impact · newest) and the
 *     consistency / module filters are server params; "Most corroborated"
 *     re-orders the loaded pages client-side; "Show 50 more" pages by offset.
 *   Gaps                    GET /api/v1/knowledge/unanswered — the questions
 *     the client assistant could not answer in the window, clustered nightly
 *     by meaning and ranked breadth first (distinct orgs, then how often, then
 *     recency). Nikhil 2026-09-14: "review questions where assistant could not
 *     find any answers.. I feel some of them should be known by the knowledge
 *     engine" — until now an abstention left no trace a rep could act on; this
 *     tab is where a rep turns such a question into knowledge. "Write this
 *     answer" seeds the unit form (the 💡 flow's fields) from the cluster and
 *     saves with POST …/units. Pure helpers in knowledge_gaps.js.
 *
 * Every listing loads with the page so each tab carries its count; ↻ reloads
 * them all (no polling). Actions are applied optimistically and rolled back
 * on error; an acted-on card stays in place until the next load.
 * Deliberately calm: no nudges, no per-rep counts, no assignment, no backlog.
 */

// Same taxonomy as CommsThread.jsx KNOWLEDGE_MODULES / ClientComms.jsx MODULES.
export const KNOWLEDGE_MODULES = ['DESIGN', 'RENDER', 'PRESENTATION', 'CATALOGUE', 'PRICING', 'PRODUCTION', 'MES', 'OTHER'];

/** Sort keys. `server` is the value sent as ?sort=. The API has no
 *  corroboration sort, so "Most corroborated" fetches the impact page and
 *  re-orders it client-side (same query → no refetch when toggling). */
export const SORTS = [
    { key: 'impact', label: 'Impact', server: 'impact' },
    { key: 'newest', label: 'Newest', server: 'newest' },
    { key: 'corroborated', label: 'Most corroborated', server: 'impact' },
];
export const DEFAULT_SORT = 'impact';
export const PAGE_LIMIT = 50;

/** The code judge's verdict → chip. Keys are the API enum. */
export const CONSISTENCY = {
    CONSISTENT: { label: 'Consistent with code', color: '#2e9e5b', background: '#eefaf2', border: '#cdebd8', hint: 'The code judge found the product code agrees with this proposal' },
    CONTRADICTED: { label: 'Contradicts code', color: '#c0392b', background: '#fdf0ee', border: '#f2c9c4', hint: 'The code judge found the product code disagrees with this proposal' },
    UNCHECKABLE: { label: 'Not code-checkable', color: '#5d6d7e', background: '#f1f4f7', border: '#dde3e8', hint: 'Not a claim the product code can confirm or deny (process, pricing, people)' },
    PENDING: { label: 'Not checked yet', color: '#95a5a6', background: '#fafbfc', border: '#e9eef2', hint: 'The code judge has not looked at this proposal yet' },
};
export const CONSISTENCY_KEYS = Object.keys(CONSISTENCY);

const fmt_ts = (d) => { try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
const cut_text = (s, n) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? t.slice(0, n - 1) + '…' : t; };
const conf_color = (c) => (c >= 0.6 ? '#2e9e5b' : c >= 0.4 ? '#8fa3c0' : '#d68910');
const title_case = (s) => String(s || '').split(/[_\s-]+/).map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : '')).join(' ');
const num_or_null = (v) => { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const unit01 = (v) => { const n = num_or_null(v); return n === null ? null : Math.min(1, Math.max(0, n)); };
const str_or_null = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const pct_of = (v) => { const n = unit01(v); return n === null ? null : Math.round(n * 100); };
/** Descending comparator that treats missing values as smallest and never returns NaN. */
const desc = (a, b) => { const x = a === null || a === undefined ? -Infinity : a; const y = b === null || b === undefined ? -Infinity : b; return x === y ? 0 : x > y ? -1 : 1; };
const ts_of = (d) => { const t = Date.parse(d); return Number.isFinite(t) ? t : null; };
const iso_day = (d) => { const t = ts_of(d); return t === null ? null : new Date(t).toISOString().slice(0, 10); };

/** Comma / semicolon / newline separated trigger phrases → trimmed list. One
 *  definition, in knowledge_gaps.js (the gap form needs it too); re-exported
 *  here because the Proposals helpers are imported from this module. */
export { parse_triggers };

/** `?unit=<id>` — the knowledge unit an assistant citation in the Chat thread
 *  ("Based on: …" → /home/knowledge?unit=<id>) deep-links to; null when absent
 *  or blank. */
export const focused_unit_id = (search) => {
    try {
        const v = new URLSearchParams(String(search || '')).get('unit');
        return v && v.trim() ? v.trim() : null;
    } catch (e) { return null; }
};
export const unit_dom_id = (id) => `knowledge-unit-${id}`;

/** One proposal row as the page trusts it: array `triggers` / `sources` /
 *  `impactReasons`, `impact` + `consistencyConfidence` clamped to 0..1 or
 *  null, `consistency` one of the four verdicts or null, string-or-null for
 *  the note / checked-at / refines fields. Everything else passes through. */
export const normalize_proposal = (p) => ({
    ...p,
    triggers: Array.isArray(p.triggers) ? p.triggers.filter((t) => typeof t === 'string' && t.trim()) : [],
    sources: Array.isArray(p.sources) ? p.sources.filter(Boolean) : [],
    impact: unit01(p.impact),
    impactReasons: Array.isArray(p.impactReasons) ? p.impactReasons.filter((r) => typeof r === 'string' && r.trim()).map((r) => r.trim()) : [],
    consistency: CONSISTENCY[p.consistency] ? p.consistency : null,
    consistencyConfidence: unit01(p.consistencyConfidence),
    consistencyNote: str_or_null(p.consistencyNote),
    consistencyCheckedAt: str_or_null(p.consistencyCheckedAt),
    refinesUnitId: str_or_null(p.refinesUnitId),
    refinesTitle: str_or_null(p.refinesTitle),
});

const listing_count = (resp, rows) => (resp && Number.isFinite(Number(resp.count)) ? Number(resp.count) : rows.length);

/** Coerce a page of GET /knowledge/proposals ({count, offset, limit,
 *  proposals}) into {count, proposals}: rows without an id are dropped,
 *  `count` falls back to the page length when the server omits it. Server
 *  order is kept. */
export const normalize_proposals = (resp) => {
    const raw = resp && Array.isArray(resp.proposals) ? resp.proposals : [];
    const proposals = raw.filter((p) => p && p.id).map(normalize_proposal);
    return { count: listing_count(resp, proposals), proposals };
};

/** One code-contradiction row: a proposal plus what the judge grounded on
 *  (`grounding` — the code-derived units it checked against, {id, title}) and
 *  whether the automation already rejected it. */
export const normalize_contradiction = (p) => ({
    ...normalize_proposal(p),
    autoRejected: p.autoRejected === true,
    grounding: (Array.isArray(p.grounding) ? p.grounding : [])
        .filter((g) => g && g.id !== null && g.id !== undefined && g.id !== '')
        .map((g) => ({ id: String(g.id), title: str_or_null(g.title) })),
});

/** GET /knowledge/contradictions → {count, proposals}. Its rows live under
 *  `contradictions`, not `proposals` — reading the proposals key here is what
 *  rendered the tab empty against 417 live rows (2026-09-14). `proposals` is
 *  still accepted for an older backend. */
export const normalize_contradictions = (resp) => {
    const raw = resp && Array.isArray(resp.contradictions) ? resp.contradictions : resp && Array.isArray(resp.proposals) ? resp.proposals : [];
    const proposals = raw.filter((p) => p && p.id).map(normalize_contradiction);
    return { count: listing_count(resp, proposals), proposals };
};

/** "Show 50 more": the next page appended to what is loaded, a row that
 *  moved between pages kept once (in its first position), `count` the
 *  server's latest. Neither input is mutated. */
export const merge_pages = (prev, next) => {
    const seen = new Set(prev.proposals.map((p) => p.id));
    return { count: next.count, proposals: [...prev.proposals, ...next.proposals.filter((p) => !seen.has(p.id))] };
};

/** Impact as the 0–100 the pill shows; null when the row carries none. */
export const impact_pct = (p) => (p ? pct_of(p.impact) : null);
export const impact_reasons = (p) => (p ? normalize_proposal(p).impactReasons : []);

/** The consistency chip for a row: {key, label, color, …, note, confidence_pct,
 *  checked_at} or null when the row carries no verdict (older backend). */
export const consistency_chip = (p) => {
    if (!p || !CONSISTENCY[p.consistency]) return null;
    return {
        key: p.consistency,
        ...CONSISTENCY[p.consistency],
        note: str_or_null(p.consistencyNote),
        confidence_pct: pct_of(p.consistencyConfidence),
        checked_at: str_or_null(p.consistencyCheckedAt),
    };
};

/** "Refines: <title>" → {unit_id, title, search} or null. The link target is
 *  the page's own ?unit= deep link; a title without an id renders unlinked. */
export const refines_link = (p) => {
    const unit_id = p ? str_or_null(p.refinesUnitId) : null;
    const title = p ? str_or_null(p.refinesTitle) : null;
    if (!unit_id && !title) return null;
    return { unit_id, title: title || 'an existing fact', search: unit_id ? '?unit=' + encodeURIComponent(unit_id) : null };
};

/** Stable client-side order for a sort key. Impact / newest mirror the
 *  server's order (ties keep server order, so a page without impact values
 *  renders as served); corroborated is only available here. */
export const sort_proposals = (list, key) => {
    const rows = Array.isArray(list) ? [...list] : [];
    if (key === 'newest') return rows.sort((a, b) => desc(ts_of(a.createdAt), ts_of(b.createdAt)));
    if (key === 'corroborated') return rows.sort((a, b) => desc(num_or_null(a.corroborations), num_or_null(b.corroborations)) || desc(unit01(a.impact), unit01(b.impact)));
    return rows.sort((a, b) => desc(unit01(a.impact), unit01(b.impact)));
};

/** Consistency / module filters; '' (or unknown) means all. */
export const filter_proposals = (list, { consistency = '', module = '' } = {}) => (Array.isArray(list) ? list : []).filter((p) =>
    (!CONSISTENCY[consistency] || p.consistency === consistency) && (!module || p.module === module));

/** Distinct modules across rows (+ any extra), in taxonomy order, unknown
 *  modules alphabetically after. */
export const modules_in = (rows, extra = []) => {
    const set = new Set([...(Array.isArray(rows) ? rows : []).map((p) => p && p.module), ...extra].filter((m) => typeof m === 'string' && m));
    return [...set].sort((a, b) => {
        const ia = KNOWLEDGE_MODULES.indexOf(a); const ib = KNOWLEDGE_MODULES.indexOf(b);
        if (ia >= 0 && ib >= 0) return ia - ib;
        if (ia >= 0) return -1;
        if (ib >= 0) return 1;
        return a.localeCompare(b);
    });
};

export const sort_server = (key) => { const s = SORTS.find((x) => x.key === key) || SORTS.find((x) => x.key === DEFAULT_SORT); return s.server; };

/** Query for GET /knowledge/proposals: only the params the server knows
 *  (an older backend ignores them — its ValidationPipe whitelists silently). */
export const proposals_query = ({ sort = DEFAULT_SORT, consistency = '', module = '', offset = 0 } = {}) => {
    const q = { limit: PAGE_LIMIT, sort: sort_server(sort) };
    if (CONSISTENCY[consistency]) q.consistency = consistency;
    if (module) q.module = module;
    const o = num_or_null(offset);
    if (o !== null && o > 0) q.offset = Math.floor(o);
    return q;
};

/** The Contradictions tab as a markdown handover for the product team.
 *  Deterministic (dates as YYYY-MM-DD; `exported_at` only when given). */
export const contradictions_markdown = (rows, { exported_at } = {}) => {
    const list = (Array.isArray(rows) ? rows : []).filter((p) => p && p.id).map(normalize_contradiction);
    const lines = ['# Knowledge proposals that contradict the product code', ''];
    const meta = [`${list.length} proposal${list.length === 1 ? '' : 's'}`];
    const day = exported_at ? iso_day(exported_at) : null;
    if (day) meta.push(`exported ${day}`);
    meta.push('source: CSP → Knowledge → Contradictions');
    lines.push(meta.join(' · '), '');
    if (!list.length) lines.push('_None — every checked proposal agrees with the code._', '');
    list.forEach((p, i) => {
        lines.push(`## ${i + 1}. ${p.title || '(untitled)'}`);
        lines.push(`- Module: ${p.module || 'OTHER'}${p.feature ? ` · Feature: ${p.feature}` : ''}`);
        const check = [`- Code check: contradicts code${p.autoRejected ? ' · auto-rejected' : ''}`];
        const conf = pct_of(p.consistencyConfidence);
        if (conf !== null) check.push(`confidence ${conf}%`);
        const checked = iso_day(p.consistencyCheckedAt);
        if (checked) check.push(`checked ${checked}`);
        lines.push(check.join(' · '));
        if (p.grounding.length) lines.push(`- Grounded on: ${p.grounding.map((g) => `${g.title || 'a code-derived fact'} (unit ${g.id})`).join('; ')}`);
        const ref = refines_link(p);
        if (ref) lines.push(`- Refines: ${ref.title}${ref.unit_id ? ` (unit ${ref.unit_id})` : ''}`);
        const ev = [];
        const corr = num_or_null(p.corroborations);
        if (corr !== null) ev.push(`corroborated by ${corr} conversation${corr === 1 ? '' : 's'}`);
        const imp = impact_pct(p);
        if (imp !== null) ev.push(`impact ${imp}${p.impactReasons.length ? ` (${p.impactReasons.join('; ')})` : ''}`);
        if (ev.length) lines.push(`- Evidence: ${ev.join(' · ')}`);
        lines.push(`- Proposal id: ${p.id}`, '');
        lines.push('**Proposed fact**', '', String(p.body || '(no body)').trim().split('\n').map((l) => `> ${l}`).join('\n'), '');
        lines.push('**Why the code disagrees**', '', p.consistencyNote || '_No note from the code judge._', '');
        if (p.triggers.length) lines.push(`**Clients say:** ${p.triggers.join(', ')}`, '');
    });
    return lines.join('\n').trimEnd() + '\n';
};

/** The proposal as the inline edit form sees it (PromoteKnowledgeModal's
 *  form shape): triggers joined for the text input, unknown module → OTHER,
 *  clientSafe derived from the audience enum. */
export const proposal_to_form = (p) => ({
    title: (p && p.title) || '',
    module: p && KNOWLEDGE_MODULES.indexOf(p.module) >= 0 ? p.module : 'OTHER',
    feature: (p && p.feature) || '',
    body: (p && p.body) || '',
    triggers: (p && Array.isArray(p.triggers) ? p.triggers : []).join(', '),
    clientSafe: !!p && p.audience === 'CLIENT_SAFE',
});

/** Only what the rep changed, in the approve body's vocabulary. Trimmed
 *  strings; triggers compared as lists; clientSafe against the audience.
 *  `{}` when the form still equals the proposal (approve as drafted). */
export const approve_diff = (p, form) => {
    const base = proposal_to_form(p);
    const out = {};
    const title = String(form.title || '').trim();
    const feature = String(form.feature || '').trim();
    const body = String(form.body || '').trim();
    if (title !== base.title.trim()) out.title = title;
    if (form.module !== base.module) out.module = form.module;
    if (feature !== base.feature.trim()) out.feature = feature;
    if (body !== base.body.trim()) out.body = body;
    const triggers = parse_triggers(form.triggers);
    const base_triggers = parse_triggers(base.triggers);
    if (JSON.stringify(triggers) !== JSON.stringify(base_triggers)) out.triggers = triggers;
    if (!!form.clientSafe !== base.clientSafe) out.clientSafe = !!form.clientSafe;
    return out;
};

/** One-line receipt for a decided card. */
export const receipt_text = (decision) => {
    if (!decision) return '';
    if (decision.kind === 'approved') {
        if (decision.pending) return '✓ approving…';
        return decision.mergedInto ? '✓ approved — merged into an existing fact' : '✓ approved — now grounding reply suggestions';
    }
    if (decision.kind === 'rejected') {
        if (decision.pending) return '✗ rejecting…';
        return '✗ rejected' + (decision.reason ? ` — ${decision.reason}` : '');
    }
    return '';
};

/* ---------- Answer contradictions (the triage tab) ---------- */

/** ?status= for GET /knowledge/answer-contradictions. */
export const ANSWER_STATUSES = [
    { key: 'open', label: 'Open' },
    { key: 'triaged', label: 'Triaged' },
    { key: 'all', label: 'All' },
];
export const DEFAULT_ANSWER_STATUS = 'open';

/** Which assistant produced the knowledge-base answer. Keys are the API enum. */
export const ANSWER_TIERS = {
    client: { label: 'client-tier answer', color: '#2e9e5b', background: '#eefaf2', border: '#cdebd8', hint: 'What the client-facing assistant answers — built from client-safe facts only' },
    rep: { label: 'rep-tier answer', color: '#8a6d1d', background: '#fff8e6', border: '#f0d48a', hint: 'What the rep-facing assistant answers — internal facts included' },
};

/** The rep's verdict on a case → button + receipt. Keys are the API enum;
 *  `title` is the button hover text and states what the verdict does. */
export const VERDICTS = {
    KB_RIGHT: { label: 'Knowledge is right', color: 'primary', tone: '#2f6fed', title: "Knowledge is right — confirms the cited units and marks the rep's answer as the outdated one" },
    REP_RIGHT: { label: 'Rep was right', color: 'success', tone: '#2e9e5b', title: "Rep was right — flags the cited units and turns the rep's answer into a new rep-attested knowledge unit" },
    BOTH: { label: 'Both, depends on context', color: 'info', tone: '#17869c', title: "Both hold in different situations — confirms the units and adds the rep's note as an addendum" },
    UNCLEAR: { label: 'Unclear', color: 'light', tone: '#7f8c8d', title: 'Cannot tell from this exchange — records the case as unclear; nothing in the knowledge base changes' },
};
export const VERDICT_KEYS = Object.keys(VERDICTS);
export const NO_EFFECTS_TEXT = 'no changes to the knowledge base';

const ids_of = (a) => (Array.isArray(a) ? a.filter((x) => x !== null && x !== undefined && x !== '').map(String) : []);
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const has_id = (v) => v !== null && v !== undefined && v !== '';

/** A stored verdict: {kind, note, byName, at} with a known kind, else null. */
export const normalize_verdict = (v) => (v && VERDICTS[v.kind]
    ? { kind: v.kind, note: str_or_null(v.note), byName: str_or_null(v.byName), at: str_or_null(v.at) }
    : null);

/** A cited unit: id required (it is the ?unit= link); corroboration passes
 *  through as a number or a level name — the chip renders either. */
export const normalize_citation = (c) => {
    if (!c || !has_id(c.id)) return null;
    const n = num_or_null(c.corroboration);
    return { id: String(c.id), title: str_or_null(c.title) || 'an existing fact', corroboration: n !== null ? n : str_or_null(c.corroboration), status: str_or_null(c.status) };
};

/** One case as the tab trusts it: strings trimmed-or-null, citations with
 *  ids, tier one of client / rep or null, verdict validated. Passes the rest through. */
export const normalize_case = (c) => ({
    ...c,
    caseId: String(c.caseId),
    module: str_or_null(c.module),
    portal: str_or_null(c.portal),
    clientName: str_or_null(c.clientName),
    runDate: str_or_null(c.runDate),
    question: str_or_null(c.question) || '',
    repAnswer: str_or_null(c.repAnswer) || '',
    repQuote: str_or_null(c.repQuote),
    kbAnswer: str_or_null(c.kbAnswer) || '',
    kbCitations: (Array.isArray(c.kbCitations) ? c.kbCitations : []).map(normalize_citation).filter(Boolean),
    tier: ANSWER_TIERS[c.tier] ? c.tier : null,
    judgeNote: str_or_null(c.judgeNote),
    verdict: normalize_verdict(c.verdict),
});

/** {items, counts:{open, triaged}} — rows without a caseId are dropped; a
 *  count the server omits falls back to the loaded page. Server order kept. */
export const normalize_cases = (resp) => {
    const raw = resp && Array.isArray(resp.items) ? resp.items : [];
    const items = raw.filter((c) => c && has_id(c.caseId)).map(normalize_case);
    const counts = resp && resp.counts ? resp.counts : {};
    const open = num_or_null(counts.open);
    const triaged = num_or_null(counts.triaged);
    return {
        items,
        counts: {
            open: open === null ? items.filter((c) => !c.verdict).length : open,
            triaged: triaged === null ? items.filter((c) => c.verdict).length : triaged,
        },
    };
};

export const cases_query = ({ status = DEFAULT_ANSWER_STATUS } = {}) => ({
    status: ANSWER_STATUSES.some((s) => s.key === status) ? status : DEFAULT_ANSWER_STATUS,
    limit: PAGE_LIMIT,
});
/** The page's module filter, applied client-side (the endpoint has no module param). */
export const filter_cases = (list, { module = '' } = {}) => (Array.isArray(list) ? list : []).filter((c) => !module || c.module === module);
export const verdict_path = (case_id) => `/api/v1/knowledge/answer-contradictions/${encodeURIComponent(case_id)}/verdict`;
/** POST body — the note only when the rep wrote one. */
export const verdict_body = (kind, note) => { const n = str_or_null(note); return n ? { kind, note: n } : { kind }; };
export const normalize_effects = (e) => ({
    unitsConfirmed: ids_of(e && e.unitsConfirmed),
    unitsFlagged: ids_of(e && e.unitsFlagged),
    proposalCreated: e && has_id(e.proposalCreated) ? String(e.proposalCreated) : null,
});
/** "confirmed 2 units, flagged 1 unit, new unit created" as parts, plus the
 *  new unit's id for the link. */
export const effects_summary = (effects) => {
    const e = normalize_effects(effects);
    const parts = [];
    if (e.unitsConfirmed.length) parts.push(`confirmed ${plural(e.unitsConfirmed.length, 'unit')}`);
    if (e.unitsFlagged.length) parts.push(`flagged ${plural(e.unitsFlagged.length, 'unit')}`);
    if (e.proposalCreated) parts.push('new unit created');
    return { parts, proposal_id: e.proposalCreated };
};
export const effects_text = (effects) => { const s = effects_summary(effects); return s.parts.length ? s.parts.join(', ') : NO_EFFECTS_TEXT; };
/** "Knowledge is right · Rajini · 14 Sep 10:02" — by / when only when known. */
export const verdict_line = (v) => {
    if (!v || !VERDICTS[v.kind]) return '';
    const bits = [VERDICTS[v.kind].label];
    if (str_or_null(v.byName)) bits.push(v.byName.trim());
    if (ts_of(v.at) !== null) bits.push(fmt_ts(v.at));
    return bits.join(' · ');
};
export const empty_cases_text = (status, filtered) => {
    if (filtered) return 'Nothing matches this module.';
    if (status === 'triaged') return 'No triaged cases yet.';
    if (status === 'all') return 'No cases yet.';
    return 'No open cases — every checked answer agreed with what the rep said.';
};
export const case_dom_id = (id) => `answer-case-${id}`;
export const unit_search = (id) => '?unit=' + encodeURIComponent(id);
/** Corroboration chip for a cited unit: a count or a level name; null when absent. */
export const corroboration_chip = (c) => {
    if (typeof c === 'number' && Number.isFinite(c)) return { label: `corroboration ${c}`, hint: 'Independent sources that back this fact' };
    const s = str_or_null(c);
    return s ? { label: title_case(s), hint: 'How this fact is corroborated' } : null;
};

/** Clipboard with the execCommand fallback; false when neither worked. */
const copy_text = async (text) => {
    try {
        if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(text); return true; }
    } catch (e) { /* fall through to the textarea route */ }
    try {
        const ta = document.createElement('textarea');
        ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        return !!ok;
    } catch (e) { return false; }
};

const S = {
    pill: { fontSize: 10.5, fontWeight: 700, color: '#fff', background: '#2c3e50', borderRadius: 10, padding: '1px 8px', lineHeight: '16px', whiteSpace: 'nowrap', letterSpacing: 0.3 },
    badge: { fontSize: 10.5, color: '#2f6fed', background: '#eef3ff', border: '1px solid #d9e6ff', borderRadius: 10, padding: '1px 8px', lineHeight: '16px', whiteSpace: 'nowrap' },
    impact: { fontSize: 10.5, color: '#4a3f8f', background: '#f1eefc', border: '1px solid #ddd6f7', borderRadius: 10, padding: '1px 8px', lineHeight: '16px', whiteSpace: 'nowrap' },
    chip: (active) => ({ border: '1px solid ' + (active ? '#2c3e50' : '#d5dbe1'), background: active ? '#2c3e50' : '#fff', color: active ? '#fff' : '#4a5866', borderRadius: 12, padding: '1px 10px', fontSize: 11.5, cursor: 'pointer', lineHeight: '18px', whiteSpace: 'nowrap' }),
    tab: (active) => ({ border: '1px solid ' + (active ? '#2f6fed' : '#d5dbe1'), background: active ? '#2f6fed' : '#fff', color: active ? '#fff' : '#4a5866', borderRadius: 14, padding: '3px 13px', fontSize: 12, cursor: 'pointer', lineHeight: '18px', whiteSpace: 'nowrap' }),
    group: { display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' },
    group_label: { fontSize: 10.5, color: '#95a5a6', textTransform: 'uppercase', letterSpacing: 0.6, marginRight: 2, whiteSpace: 'nowrap' },
    muted: { fontSize: 11, color: '#95a5a6' },
    why: { fontSize: 11.5, color: '#5d6d7e', marginTop: 6, lineHeight: 1.4 },
    label: { fontSize: 11, color: '#7f8c8d', marginBottom: 2, marginTop: 8 },
    hint: { color: '#b0b8bd' },
    field: { fontSize: 13 },
    card: { background: '#fff', border: '1px solid #e3e8ec', borderRadius: 10, padding: '12px 16px', marginBottom: 10 },
    receipt: { background: '#f8fafb', border: '1px solid #e9eef2', borderRadius: 10, padding: '7px 16px', marginBottom: 8, fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' },
    // Answer-contradiction cards: the two answers side by side (stacked when narrow).
    column: { flex: '1 1 280px', minWidth: 0, background: '#f8fafb', border: '1px solid #eef2f5', borderRadius: 8, padding: '8px 10px' },
    column_label: { fontSize: 10.5, color: '#95a5a6', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 },
    body: { fontSize: 12.5, color: '#33414e', whiteSpace: 'pre-wrap', lineHeight: 1.45 },
    quote: { fontSize: 11.5, color: '#7f8c8d', borderLeft: '3px solid #d6dbdf', padding: '2px 8px', marginTop: 6, whiteSpace: 'pre-wrap' },
    // The gap card's "Code" block: what the gaps step derived from the product code (the left rule takes the outcome's tone).
    code: { marginTop: 8, background: '#f8fafb', border: '1px solid #eef2f5', borderRadius: 8, padding: '8px 10px' },
    code_path: { fontFamily: 'SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace', fontSize: 11, color: '#5d6d7e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
};

/** "impact 72" — click (or hover) for the reasons behind the number. */
function ImpactPill({ proposal, open, on_toggle }) {
    const pct = impact_pct(proposal);
    if (pct === null) return null;
    const reasons = impact_reasons(proposal);
    const expandable = reasons.length > 0 && typeof on_toggle === 'function';
    return (
        <span style={{ ...S.impact, cursor: expandable ? 'pointer' : 'default' }} onClick={expandable ? on_toggle : undefined}
            title={reasons.length ? reasons.join('\n') : 'Impact — how much approving this fact would help, 0–100'}>
            impact <b>{pct}</b>{expandable ? <span style={{ color: '#9b8fd4', marginLeft: 3 }}>{open ? '▾' : '▸'}</span> : null}
        </span>
    );
}

/** The code judge's verdict; click for the note + confidence. */
function ConsistencyChip({ chip, open, on_toggle }) {
    if (!chip) return null;
    const expandable = typeof on_toggle === 'function' && !!(chip.note || chip.confidence_pct !== null || chip.checked_at);
    return (
        <span style={{ ...S.badge, color: chip.color, background: chip.background, borderColor: chip.border, cursor: expandable ? 'pointer' : 'default' }}
            onClick={expandable ? on_toggle : undefined} title={chip.note || chip.hint}>
            {chip.label}{expandable ? <span style={{ opacity: 0.6, marginLeft: 3 }}>{open ? '▾' : '▸'}</span> : null}
        </span>
    );
}

/** The expanded verdict: "Why: <note> · confidence 82% · checked 12 Sep 14:02". */
function WhyLine({ chip }) {
    const bits = [];
    if (chip.confidence_pct !== null) bits.push(`confidence ${chip.confidence_pct}%`);
    if (chip.checked_at) bits.push(`checked ${fmt_ts(chip.checked_at)}`);
    return (
        <div style={{ ...S.why, borderLeft: `3px solid ${chip.border}`, padding: '2px 8px' }}>
            <span style={{ color: chip.color, fontWeight: 600 }}>Why: </span>
            <span style={{ whiteSpace: 'pre-wrap' }}>{chip.note || chip.hint}</span>
            {bits.length ? <span style={{ color: '#95a5a6' }}> · {bits.join(' · ')}</span> : null}
        </div>
    );
}

/** "Refines: <title>" → the page's own ?unit= deep link (FocusedUnit pins the
 *  approved fact above the queue). */
function RefinesLine({ proposal, on_navigate }) {
    const location = useLocation();
    const ref = refines_link(proposal);
    if (!ref) return null;
    return (
        <div style={{ fontSize: 11.5, color: '#7f8c8d', marginTop: 6 }}>
            Refines:{' '}
            {ref.search ? (
                <Link to={{ pathname: location.pathname, search: ref.search }} onClick={on_navigate} style={{ color: '#2f6fed' }} title='Open the fact this proposal sharpens'>{ref.title}</Link>
            ) : <span style={{ color: '#5d6d7e' }}>{ref.title}</span>}
        </div>
    );
}

/** The conversation excerpts a proposal rests on: source type · date · quote.
 *  Two shown; the rest behind "show N more". */
function Sources({ sources }) {
    const [all, set_all] = useState(false);
    if (!sources.length) return null;
    const shown = all ? sources : sources.slice(0, 2);
    const hidden = sources.length - shown.length;
    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ ...S.muted, marginBottom: 3 }}>Said in conversation</div>
            {shown.map((s, i) => (
                <div key={i} style={{ fontSize: 11.5, color: '#7f8c8d', borderLeft: '3px solid #d6dbdf', padding: '2px 8px', background: '#f6f8f9', borderRadius: 4, marginBottom: 4 }}>
                    <span style={{ color: '#95a5a6' }}>{title_case(s.sourceType) || 'Conversation'}{s.occurredAt ? ` · ${fmt_ts(s.occurredAt)}` : ''}</span>
                    {s.quote ? <div style={{ whiteSpace: 'pre-wrap', color: '#5d6d7e' }}>“{cut_text(s.quote, all ? 600 : 240)}”</div> : null}
                </div>
            ))}
            {sources.length > 2 ? (
                <span onClick={() => set_all((a) => !a)} style={{ fontSize: 11, color: '#2f6fed', cursor: 'pointer' }}>
                    {all ? 'show fewer' : `show ${hidden} more`}
                </span>
            ) : null}
        </div>
    );
}

/** The unit form's fields — title · module · feature / area · the fact · how
 *  clients phrase it · client-safe — exactly as the Chat thread's 💡 modal
 *  shows them, so a rep meets one form for a fact wherever they write it
 *  (Proposals inline edit, Gaps "Write this answer"). `form` is that modal's
 *  shape (proposal_to_form / gap_form_seed); `on_change(key)` the input handler. */
function UnitFields({ form, on_change }) {
    return (
        <>
            <div style={S.label}>Title <span style={S.hint}>— one searchable statement of the fact</span></div>
            <Input value={form.title} onChange={on_change('title')} style={S.field} maxLength={200} />
            <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: '0 0 180px' }}>
                    <div style={S.label}>Module</div>
                    <Input type='select' value={form.module} onChange={on_change('module')} style={S.field}>
                        {KNOWLEDGE_MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Input>
                </div>
                <div style={{ flex: 1 }}>
                    <div style={S.label}>Feature / area</div>
                    <Input value={form.feature} onChange={on_change('feature')} style={S.field} maxLength={120} placeholder='e.g. drawer hardware templates' />
                </div>
            </div>
            <div style={S.label}>The fact <span style={S.hint}>— general case; no client, project or person names</span></div>
            <Input type='textarea' rows={6} value={form.body} onChange={on_change('body')} style={S.field} maxLength={4000} />
            <div style={S.label}>How clients phrase it <span style={S.hint}>— comma-separated; used to find this fact</span></div>
            <Input value={form.triggers} onChange={on_change('triggers')} style={S.field} placeholder='cnc program missing, cut list blank, …' />
            <label style={{ fontSize: 12, marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type='checkbox' checked={!!form.clientSafe} onChange={on_change('clientSafe')} />
                Safe to paraphrase to clients <span style={{ color: '#95a5a6' }}>(untick for internal-only: defect workarounds, pricing internals, promises)</span>
            </label>
        </>
    );
}

/** One PROPOSED unit. View mode shows the drafted fact; Edit swaps the same
 *  fields for inputs (UnitFields — PromoteKnowledgeModal's form). Approve sends
 *  only the diff; Reject takes an optional reason (KnowledgeChip flag-input pattern). */
function ProposalCard({ proposal, decision, on_decide }) {
    const [editing, set_editing] = useState(false);
    const [form, set_form] = useState(() => proposal_to_form(proposal));
    const [rejecting, set_rejecting] = useState(false);
    const [reason, set_reason] = useState('');
    const [show_impact, set_show_impact] = useState(false);
    const chip = consistency_chip(proposal);
    // A contradiction is the one verdict that changes the decision — its note starts open.
    const [show_why, set_show_why] = useState(!!chip && chip.key === 'CONTRADICTED');
    const p = proposal;

    if (decision) {
        return (
            <div style={S.receipt}>
                <span style={{ color: decision.kind === 'approved' ? '#2e9e5b' : '#c0392b', fontWeight: 600, whiteSpace: 'nowrap' }}>{receipt_text(decision)}</span>
                <span style={{ color: '#7f8c8d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    <span style={{ color: '#b0b8bd' }}>{p.module} · </span>{cut_text((decision.unit && decision.unit.title) || p.title, 120)}
                </span>
                {decision.pending ? <Spinner size='sm' style={{ width: 12, height: 12, flex: 'none' }} /> : null}
            </div>
        );
    }

    const upd = (k) => (e) => {
        const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        set_form((f) => ({ ...f, [k]: v }));
    };
    const diff = editing ? approve_diff(p, form) : {};
    const edited = Object.keys(diff).length > 0;
    const invalid = editing && (!String(form.title || '').trim() || !String(form.body || '').trim());
    const approve = () => {
        if (invalid) { window.alert('Title and the fact itself are required.'); return; }
        on_decide(p.id, 'approve', diff);
    };
    const reject = () => on_decide(p.id, 'reject', reason.trim() ? { reason: reason.trim() } : {});
    const pct = Math.round((Number(p.confidence) || 0) * 100);
    const corroborations = Number.isFinite(Number(p.corroborations)) ? Number(p.corroborations) : null;
    const client_safe = editing ? !!form.clientSafe : p.audience === 'CLIENT_SAFE';
    const reasons = impact_reasons(p);
    // The verdict was about the drafted wording; once edited it no longer vouches for the text.
    const matches_code = !!chip && chip.key === 'CONSISTENT' && !edited;

    return (
        <div id={unit_dom_id(p.id)} style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={S.pill}>{editing ? form.module : p.module}</span>
                {!editing && p.feature ? <span style={{ fontSize: 12, color: '#5d6d7e' }}>{p.feature}</span> : null}
                <span title={`confidence ${pct}%`} style={{ width: 7, height: 7, borderRadius: 4, background: conf_color(Number(p.confidence) || 0), flex: 'none' }} />
                <ImpactPill proposal={p} open={show_impact} on_toggle={() => set_show_impact((v) => !v)} />
                <ConsistencyChip chip={chip} open={show_why} on_toggle={() => set_show_why((v) => !v)} />
                {corroborations !== null ? (
                    <span style={S.badge} title='Distinct conversations the miner found this fact in'>
                        corroborated by {corroborations} conversation{corroborations === 1 ? '' : 's'}
                    </span>
                ) : null}
                <span style={{ ...S.badge, color: client_safe ? '#2e9e5b' : '#8a6d1d', background: client_safe ? '#eefaf2' : '#fff8e6', borderColor: client_safe ? '#cdebd8' : '#f0d48a' }}
                    title={client_safe ? 'May be paraphrased to clients in reply suggestions' : 'Internal only — never surfaced to clients'}>
                    {client_safe ? 'client-safe' : 'internal'}
                </span>
                <span style={{ ...S.muted, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{p.createdAt ? `proposed ${fmt_ts(p.createdAt)}` : ''}</span>
            </div>
            {show_impact && reasons.length ? (
                <div style={S.why}><span style={{ color: '#4a3f8f', fontWeight: 600 }}>Impact: </span>{reasons.join(' · ')}</div>
            ) : null}
            {show_why && chip ? <WhyLine chip={chip} /> : null}

            {!editing ? (
                <>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#233240', marginTop: 8 }}>{p.title || <i style={{ color: '#95a5a6' }}>(untitled)</i>}</div>
                    <div style={{ fontSize: 12.5, color: '#33414e', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 4 }}>{p.body || <i style={{ color: '#95a5a6' }}>(no body)</i>}</div>
                    {p.triggers.length ? (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
                            <span style={S.muted}>Clients say:</span>
                            {p.triggers.map((t, i) => (
                                <span key={i} style={{ fontSize: 11, color: '#4a5a6a', background: '#f1f4f7', border: '1px solid #e3e8ec', borderRadius: 10, padding: '0 8px', lineHeight: '17px' }}>{t}</span>
                            ))}
                        </div>
                    ) : null}
                    <RefinesLine proposal={p} />
                </>
            ) : <UnitFields form={form} on_change={upd} />}

            <Sources sources={p.sources} />

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                <Button size='sm' color='success' onClick={approve} disabled={invalid} style={{ fontSize: 12, padding: '2px 12px' }}
                    title={matches_code ? 'The code judge found this consistent with the product code' : undefined}>
                    {editing && edited ? 'Approve with edits' : 'Approve'}
                </Button>
                {matches_code ? <span style={{ fontSize: 11, color: '#2e9e5b', marginRight: 4 }} title='The code judge found this consistent with the product code'>— matches code</span> : null}
                {!rejecting ? (
                    <Button size='sm' color='danger' outline onClick={() => set_rejecting(true)} style={{ fontSize: 12, padding: '2px 12px' }}>Reject</Button>
                ) : null}
                {!editing ? (
                    <Button size='sm' color='light' onClick={() => set_editing(true)} style={{ fontSize: 12, padding: '2px 12px', border: '1px solid #dde3e8' }}>Edit</Button>
                ) : (
                    <Button size='sm' color='light' onClick={() => { set_editing(false); set_form(proposal_to_form(p)); }} style={{ fontSize: 12, padding: '2px 12px', border: '1px solid #dde3e8' }}>
                        {edited ? 'Discard edits' : 'Done editing'}
                    </Button>
                )}
                {editing && edited ? <span style={S.muted}>changed: {Object.keys(diff).join(', ')}</span> : null}
            </div>
            {rejecting ? (
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    <Input bsSize='sm' value={reason} placeholder='Why reject? (optional — wrong, not reusable, duplicate…)' autoFocus
                        onChange={(e) => set_reason(e.target.value)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') { e.preventDefault(); reject(); }
                            else if (e.key === 'Escape') { e.preventDefault(); set_rejecting(false); }
                        }}
                        style={{ fontSize: 11, padding: '2px 6px', height: 24 }} />
                    <Button size='sm' color='danger' outline style={{ fontSize: 11, padding: '0 8px' }} onClick={reject}>Reject</Button>
                    <Button size='sm' color='light' style={{ fontSize: 11, padding: '0 8px' }} onClick={() => set_rejecting(false)}>Cancel</Button>
                </div>
            ) : null}
        </div>
    );
}

/** The fact an assistant answer cited (Chat thread "Based on: …" →
 *  /home/knowledge?unit=<id>). Cited units are usually approved facts, not
 *  PROPOSED rows, so they are fetched by id (GET /knowledge/units/:id) and
 *  pinned above the queue, scrolled into view and flashed. When the id IS a
 *  live proposal, that card is focused instead and this one just says so. */
function FocusedUnit({ unit_id, in_queue, on_clear }) {
    const [unit, set_unit] = useState(null); // null = loading; false = failed
    const ref = useRef(null);
    useEffect(() => {
        let alive = true;
        set_unit(null);
        cs_analysis_fetch({ path: `/api/v1/knowledge/units/${encodeURIComponent(unit_id)}` })
            .then((r) => { if (alive) set_unit(r && r.title ? r : false); })
            .catch(() => { if (alive) set_unit(false); });
        return () => { alive = false; };
    }, [unit_id]);
    useEffect(() => {
        if (unit === null) return undefined;
        const el = (in_queue && document.getElementById(unit_dom_id(unit_id))) || ref.current;
        if (!el) return undefined;
        if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
        const prev = el.style.boxShadow;
        el.style.transition = 'box-shadow 1.6s';
        el.style.boxShadow = '0 0 0 3px #ffe08a';
        const t = setTimeout(() => { el.style.boxShadow = prev; }, 1800);
        return () => clearTimeout(t);
    }, [unit_id, in_queue, unit]);
    const pct = unit && unit.confidence != null ? Math.round((Number(unit.confidence) || 0) * 100) : null;
    return (
        <div ref={ref} style={{ ...S.card, borderColor: '#d9c9ff', background: '#faf8ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ ...S.badge, color: '#6c4bb5', background: '#f3efff', borderColor: '#ddd2f6' }} title='A fact the Infurnia Assistant cited in a client chat'>
                    <span role='img' aria-label='knowledge'>📘</span> cited fact
                </span>
                {unit ? <span style={S.pill}>{unit.module}</span> : null}
                {unit && unit.feature ? <span style={{ fontSize: 12, color: '#5d6d7e' }}>{unit.feature}</span> : null}
                {unit && unit.status ? <span style={S.badge}>{unit.status}</span> : null}
                {pct !== null ? <span title={`confidence ${pct}%`} style={{ width: 7, height: 7, borderRadius: 4, background: conf_color(Number(unit.confidence) || 0), flex: 'none' }} /> : null}
                {unit && unit.audience ? <span style={S.badge}>{unit.audience === 'CLIENT_SAFE' ? 'client-safe' : 'internal'}</span> : null}
                <span onClick={on_clear} title='Back to the review queue' style={{ marginLeft: 'auto', cursor: 'pointer', color: '#95a5a6', fontSize: 12 }}>✕ clear</span>
            </div>
            {unit === null ? <div style={{ marginTop: 8 }}><Spinner size='sm' style={{ width: 12, height: 12 }} /></div>
                : unit === false ? <div style={{ marginTop: 8, fontSize: 12.5, color: '#c0392b' }}>Could not load fact {unit_id} — it may have been retired.</div>
                    : (
                        <>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#233240', marginTop: 8 }}>{unit.title}</div>
                            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5, color: '#33414e', marginTop: 4 }}>{unit.body}</div>
                            {Array.isArray(unit.triggers) && unit.triggers.length ? (
                                <div style={{ fontSize: 11.5, color: '#7f8c8d', marginTop: 6 }}>Clients phrase it as: {unit.triggers.join(', ')}</div>
                            ) : null}
                            {in_queue ? <div style={{ fontSize: 11.5, color: '#8a6d1d', marginTop: 6 }}>This fact is still a proposal — review it in the highlighted card below.</div> : null}
                        </>
                    )}
        </div>
    );
}

/** The code-derived facts the judge checked a contradiction against — each
 *  the page's own ?unit= deep link (FocusedUnit pins it above Proposals). */
function GroundingLine({ grounding, on_navigate }) {
    const location = useLocation();
    return (
        <div style={{ fontSize: 11.5, color: '#7f8c8d', marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <span>Checked against:</span>
            {grounding.map((g) => (
                <Link key={g.id} to={{ pathname: location.pathname, search: unit_search(g.id) }} onClick={on_navigate} style={{ color: '#2f6fed' }} title='Open the code-derived fact the judge relied on'>
                    {g.title || `unit ${g.id}`}
                </Link>
            ))}
        </div>
    );
}

const CONTRADICTIONS_INTRO = 'Proposals the code judge rejected because the product code says otherwise — the confident ones automatically, the rest still waiting in Proposals. Listed with the judge\'s note and the code-derived facts it checked against, for awareness and the product handover. Nothing is sent automatically.';

/** Code contradictions: proposals the code judge found the product code
 *  disagrees with (GET /knowledge/contradictions — loaded by the page so the
 *  tab carries its count). The confident ones were auto-rejected by the
 *  automation; the rest still sit in Proposals ("review in queue" opens it
 *  filtered). Listed with the judge's note and the code-derived facts it
 *  grounded on, for awareness and the product handover. Copy-as-markdown
 *  only; nothing is posted anywhere. */
function Contradictions({ data, error, on_review_in_queue, on_navigate }) {
    const [copied, set_copied] = useState(null); // null | 'ok' | 'failed'
    const [show_md, set_show_md] = useState(false);
    useEffect(() => {
        if (!copied) return undefined;
        const t = setTimeout(() => set_copied(null), 2200);
        return () => clearTimeout(t);
    }, [copied]);
    const rows = data ? data.proposals : [];
    const md = data ? contradictions_markdown(rows, { exported_at: new Date() }) : '';
    const copy = async () => { set_copied((await copy_text(md)) ? 'ok' : 'failed'); };

    return (
        <div style={{ maxWidth: 920 }}>
            <div style={{ ...S.card, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 12, color: '#5d6d7e' }}>
                <span>{CONTRADICTIONS_INTRO}{data && data.count > rows.length ? ` Showing the newest ${rows.length} of ${data.count}.` : ''}</span>
                {data && rows.length ? (
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <Button size='sm' color='light' onClick={copy} style={{ fontSize: 12, padding: '2px 12px', border: '1px solid #dde3e8' }}>
                            {copied === 'ok' ? '✓ copied' : copied === 'failed' ? 'could not copy — use the text below' : 'Copy as markdown'}
                        </Button>
                        <span onClick={() => set_show_md((v) => !v)} style={{ cursor: 'pointer', color: '#2f6fed', fontSize: 11.5 }}>{show_md ? 'hide markdown' : 'show markdown'}</span>
                        <span onClick={on_review_in_queue} style={{ cursor: 'pointer', color: '#2f6fed', fontSize: 11.5 }}>review in queue →</span>
                    </span>
                ) : null}
            </div>
            {show_md && md ? <Input type='textarea' readOnly value={md} rows={14} style={{ fontSize: 11.5, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', marginBottom: 10 }} onFocus={(e) => e.target.select()} /> : null}
            {error ? <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>Could not load contradictions — {cut_text(error, 200)}</div> : null}
            {data === null ? <div style={{ padding: 8 }}><Spinner size='sm' style={{ width: 12, height: 12 }} /></div> : null}
            {data !== null && !error && rows.length === 0 ? (
                <div style={{ ...S.card, color: '#7f8c8d', fontSize: 13, textAlign: 'center', padding: '28px 16px' }}>
                    No contradictions — every checked proposal agrees with the code.
                </div>
            ) : null}
            {rows.map((p) => {
                const chip = consistency_chip(p) || { ...CONSISTENCY.CONTRADICTED, key: 'CONTRADICTED', note: str_or_null(p.consistencyNote), confidence_pct: pct_of(p.consistencyConfidence), checked_at: p.consistencyCheckedAt };
                const corroborations = Number.isFinite(Number(p.corroborations)) ? Number(p.corroborations) : null;
                const reasons = impact_reasons(p);
                return (
                    <div key={p.id} id={unit_dom_id(p.id)} style={S.card}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={S.pill}>{p.module}</span>
                            {p.feature ? <span style={{ fontSize: 12, color: '#5d6d7e' }}>{p.feature}</span> : null}
                            <ImpactPill proposal={p} open={false} />
                            <ConsistencyChip chip={chip} open={false} />
                            <span style={{ ...S.badge, color: '#5d6d7e', background: '#f1f4f7', borderColor: '#dde3e8' }}
                                title={p.autoRejected ? 'The automation retired this proposal on the judge\'s verdict' : 'The judge was not confident enough to retire it — it still waits in Proposals'}>
                                {p.autoRejected ? 'auto-rejected' : 'still a proposal'}
                            </span>
                            {corroborations !== null ? <span style={S.badge}>corroborated by {corroborations} conversation{corroborations === 1 ? '' : 's'}</span> : null}
                            <span style={{ ...S.muted, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{p.createdAt ? `proposed ${fmt_ts(p.createdAt)}` : ''}</span>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#233240', marginTop: 8 }}>{p.title || <i style={{ color: '#95a5a6' }}>(untitled)</i>}</div>
                        <div style={{ fontSize: 12.5, color: '#33414e', whiteSpace: 'pre-wrap', lineHeight: 1.45, marginTop: 4 }}>{p.body || <i style={{ color: '#95a5a6' }}>(no body)</i>}</div>
                        <WhyLine chip={chip} />
                        {p.grounding.length ? <GroundingLine grounding={p.grounding} on_navigate={on_navigate} /> : null}
                        {reasons.length ? <div style={S.why}><span style={{ color: '#4a3f8f', fontWeight: 600 }}>Impact: </span>{reasons.join(' · ')}</div> : null}
                        <RefinesLine proposal={p} on_navigate={on_navigate} />
                        {p.triggers.length ? <div style={{ fontSize: 11.5, color: '#7f8c8d', marginTop: 6 }}>Clients say: {p.triggers.join(', ')}</div> : null}
                    </div>
                );
            })}
        </div>
    );
}

/** One unit the knowledge-base answer rests on → the page's ?unit= deep link
 *  (FocusedUnit pins it above the queue), with its corroboration and status. */
function CitationLink({ citation, on_navigate }) {
    const location = useLocation();
    const corr = corroboration_chip(citation.corroboration);
    return (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 11.5, marginTop: 3 }}>
            <Link to={{ pathname: location.pathname, search: unit_search(citation.id) }} onClick={on_navigate} style={{ color: '#2f6fed' }} title='Open this fact in the queue'>{citation.title}</Link>
            {corr ? <span style={S.badge} title={corr.hint}>{corr.label}</span> : null}
            {citation.status ? <span style={{ ...S.badge, color: '#5d6d7e', background: '#f1f4f7', borderColor: '#dde3e8' }} title='Unit status'>{citation.status}</span> : null}
        </div>
    );
}

/** "Effect: flagged 1 unit, new unit created → view" once a verdict has landed. */
function EffectsLine({ effects, on_navigate }) {
    const location = useLocation();
    const { proposal_id } = effects_summary(effects);
    return (
        <div style={{ fontSize: 11.5, color: '#5d6d7e', marginTop: 4 }}>
            <span style={{ color: '#95a5a6' }}>Effect: </span>{effects_text(effects)}
            {proposal_id ? <> → <Link to={{ pathname: location.pathname, search: unit_search(proposal_id) }} onClick={on_navigate} style={{ color: '#2f6fed' }} title='Open the new unit'>view</Link></> : null}
        </div>
    );
}

const NOTE_PLACEHOLDER = 'Note (optional) — why, or what the right answer actually is';

/** One answer-contradiction case: the client's question, the rep's answer and
 *  the knowledge base's side by side, the judge's note, then the four verdicts
 *  with an optional note. A decided case stays in place showing the stored
 *  verdict and what it changed, until a filter change reloads the list.
 *  `local` is this session's verdict for the case ({pending} while the POST
 *  is in flight, then {verdict, effects}); `item.verdict` is the stored one. */
function AnswerCase({ item, local, on_verdict, on_navigate }) {
    const [note, set_note] = useState('');
    const c = item;
    const pending = !!(local && local.pending);
    const decided = local && !local.pending ? local : null;
    const verdict = decided ? decided.verdict : c.verdict;
    const tier = c.tier ? ANSWER_TIERS[c.tier] : null;
    return (
        <div id={case_dom_id(c.caseId)} style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {c.module ? <span style={S.pill}>{c.module}</span> : null}
                {c.clientName ? <span style={{ fontSize: 12, color: '#5d6d7e' }}>{c.clientName}</span> : null}
                {c.portal ? <span style={S.badge} title='The portal the question was asked in'>{c.portal}</span> : null}
                {tier ? <span style={{ ...S.badge, color: tier.color, background: tier.background, borderColor: tier.border }} title={tier.hint}>{tier.label}</span> : null}
                <span style={{ ...S.muted, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{c.runDate ? `run ${c.runDate}` : ''}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#233240', marginTop: 8, whiteSpace: 'pre-wrap' }}>
                {c.question || <i style={{ color: '#95a5a6' }}>(no question recorded)</i>}
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                <div style={S.column}>
                    <div style={S.column_label}>What the rep told the client</div>
                    <div style={S.body}>{c.repAnswer || <i style={{ color: '#95a5a6' }}>(no answer recorded)</i>}</div>
                    {c.repQuote ? <div style={S.quote}>“{c.repQuote}”</div> : null}
                </div>
                <div style={S.column}>
                    <div style={S.column_label}>What the knowledge base answers</div>
                    <div style={S.body}>{c.kbAnswer || <i style={{ color: '#95a5a6' }}>(no answer recorded)</i>}</div>
                    {c.kbCitations.length ? (
                        <div style={{ marginTop: 6 }}>
                            <div style={S.muted}>Based on</div>
                            {c.kbCitations.map((u) => <CitationLink key={u.id} citation={u} on_navigate={on_navigate} />)}
                        </div>
                    ) : null}
                </div>
            </div>
            {c.judgeNote ? (
                <div style={{ ...S.why, fontStyle: 'italic' }}><span style={{ color: '#95a5a6', fontStyle: 'normal' }}>Judge: </span>{c.judgeNote}</div>
            ) : null}
            {verdict ? (
                <div style={{ marginTop: 12, borderTop: '1px solid #eef2f5', paddingTop: 8 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: VERDICTS[verdict.kind].tone }}>✓ {verdict_line(verdict)}</div>
                    {verdict.note ? <div style={{ fontSize: 12, color: '#5d6d7e', whiteSpace: 'pre-wrap', marginTop: 2 }}>{verdict.note}</div> : null}
                    {decided ? <EffectsLine effects={decided.effects} on_navigate={on_navigate} /> : null}
                </div>
            ) : (
                <>
                    <Input type='textarea' rows={2} value={note} onChange={(e) => set_note(e.target.value)} disabled={pending} maxLength={2000}
                        placeholder={NOTE_PLACEHOLDER} style={{ fontSize: 12, marginTop: 10 }} />
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                        {VERDICT_KEYS.map((k) => (
                            <Button key={k} size='sm' color={VERDICTS[k].color} outline={k !== 'UNCLEAR'} data-verdict={k} title={VERDICTS[k].title} disabled={pending}
                                onClick={() => on_verdict(c.caseId, k, note)}
                                style={{ fontSize: 12, padding: '2px 12px', ...(k === 'UNCLEAR' ? { border: '1px solid #dde3e8' } : {}) }}>
                                {VERDICTS[k].label}
                            </Button>
                        ))}
                        {pending ? <Spinner size='sm' style={{ width: 12, height: 12, flex: 'none' }} /> : null}
                    </div>
                </>
            )}
        </div>
    );
}

const ANSWERS_INTRO = 'Client questions where the knowledge base answered differently from the rep. Decide who was right — each verdict confirms or flags the cited facts, and an answer the rep got right becomes a new fact.';
const ANSWERS_FOOTER = "Verdicts are recorded with your name. Knowledge is right confirms the cited facts; Rep was right flags them and turns the rep's answer into a new rep-attested fact; Both adds the rep's note as an addendum.";

/** Answer contradictions: cases where the knowledge base's answer to a real
 *  client question disagrees with what the rep told them (GET
 *  /knowledge/answer-contradictions). The rep decides who was right; each
 *  verdict (POST …/:caseId/verdict) confirms or flags the cited units and
 *  can turn the rep's answer into a new unit. Data and verdicts live in
 *  Knowledge so the tab can carry its open count from page load. */
function AnswerContradictions({ data, error, status, on_status, module, modules, on_module, verdicts, on_verdict, on_navigate }) {
    const rows = data ? filter_cases(data.items, { module }) : [];
    return (
        <div style={{ maxWidth: 920 }}>
            <div style={{ ...S.card, fontSize: 12, color: '#5d6d7e' }}>{ANSWERS_INTRO}</div>
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                <span style={S.group}>
                    <span style={S.group_label}>Show</span>
                    {ANSWER_STATUSES.map((s) => (
                        <button key={s.key} type='button' data-status={s.key} style={S.chip(status === s.key)} onClick={() => on_status(s.key)}>{s.label}</button>
                    ))}
                </span>
                {modules.length > 1 || module ? (
                    <span style={S.group}>
                        <span style={S.group_label}>Module</span>
                        <button type='button' style={S.chip(!module)} onClick={() => on_module('')}>All</button>
                        {modules.map((m) => <button key={m} type='button' style={S.chip(module === m)} onClick={() => on_module(module === m ? '' : m)}>{m}</button>)}
                    </span>
                ) : null}
            </div>
            {error ? <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>Could not load answer contradictions — {cut_text(error, 200)}</div> : null}
            {data === null ? <div style={{ padding: 8 }}><Spinner size='sm' style={{ width: 12, height: 12 }} /></div> : null}
            {data !== null && !error && rows.length === 0 ? (
                <div style={{ ...S.card, color: '#7f8c8d', fontSize: 13, textAlign: 'center', padding: '28px 16px' }}>{empty_cases_text(status, !!module)}</div>
            ) : null}
            {rows.map((c) => <AnswerCase key={c.caseId} item={c} local={verdicts[c.caseId] || null} on_verdict={on_verdict} on_navigate={on_navigate} />)}
            {rows.length ? <div style={{ fontSize: 10.5, color: '#b0b8bd', marginTop: 6 }}>{ANSWERS_FOOTER}</div> : null}
        </div>
    );
}

/* ---------- Client review (the review queue tab) ---------- */

/** The three lists GET /knowledge/client-review returns, in the order the
 *  tab shows them; `empty` is the quiet line for an empty list. */
export const REVIEW_SECTIONS = [
    { key: 'sample', empty: 'Nothing sampled this week.' },
    { key: 'unsure', empty: 'The safety screen was sure about everything it saw.' },
    { key: 'demoted', empty: 'Nothing has come off the client tier lately.' },
];
export const REVIEW_SECTION_KEYS = REVIEW_SECTIONS.map((s) => s.key);

const int_or_zero = (v) => { const n = num_or_null(v); return n === null ? 0 : Math.max(0, Math.round(n)); };
const days_text = (n) => { const d = num_or_null(n); return d && d > 0 ? `in the last ${d} day${d === 1 ? '' : 's'}` : 'recently'; };

/** Evidence counts, each a non-negative integer (0 when the server omits it). */
export const normalize_evidence = (e) => ({
    clients: int_or_zero(e && e.clients),
    uses: int_or_zero(e && e.uses),
    flags: int_or_zero(e && e.flags),
    unhelpfulUsers: int_or_zero(e && e.unhelpfulUsers),
    helpful: int_or_zero(e && e.helpful),
});

/** One review row as the tab trusts it: id as a string, strings trimmed-or-
 *  null, triggers / reasons as clean lists, numbers clamped, the evidence
 *  block always present. The section-specific fields (reasons · reason ·
 *  promotionConfidence) normalise whether or not they apply to the row. */
export const normalize_review_item = (u) => ({
    ...u,
    id: String(u.id),
    module: str_or_null(u.module),
    feature: str_or_null(u.feature),
    title: str_or_null(u.title) || '',
    body: str_or_null(u.body) || '',
    triggers: Array.isArray(u.triggers) ? u.triggers.filter((t) => typeof t === 'string' && t.trim()) : [],
    audience: str_or_null(u.audience),
    corroboration: str_or_null(u.corroboration),
    status: str_or_null(u.status),
    confidence: unit01(u.confidence),
    consistency: CONSISTENCY[u.consistency] ? u.consistency : null,
    consistencyConfidence: unit01(u.consistencyConfidence),
    clientSafeAt: str_or_null(u.clientSafeAt),
    clientDemotedAt: str_or_null(u.clientDemotedAt),
    createdAt: str_or_null(u.createdAt),
    evidence: normalize_evidence(u.evidence),
    at: str_or_null(u.at),
    via: str_or_null(u.via),
    reasons: Array.isArray(u.reasons) ? u.reasons.filter((r) => typeof r === 'string' && r.trim()).map((r) => r.trim()) : [],
    reason: str_or_null(u.reason),
    promotionConfidence: unit01(u.promotionConfidence),
});

const review_list = (v) => (Array.isArray(v) ? v : []).filter((u) => u && has_id(u.id)).map(normalize_review_item);

/** GET /knowledge/client-review → {isoWeek, sampleRate, windows, counts,
 *  sample, unsure, demoted}. The counts are the lists' lengths (rows without
 *  an id are dropped); `sampledFrom` is the server's pool size. Junk → empty. */
export const normalize_client_review = (resp) => {
    const r = resp && typeof resp === 'object' ? resp : {};
    const w = r.windows && typeof r.windows === 'object' ? r.windows : {};
    const c = r.counts && typeof r.counts === 'object' ? r.counts : {};
    const sample = review_list(r.sample);
    const unsure = review_list(r.unsure);
    const demoted = review_list(r.demoted);
    return {
        isoWeek: str_or_null(r.isoWeek),
        sampleRate: unit01(r.sampleRate),
        windows: { sampleDays: num_or_null(w.sampleDays), demotedDays: num_or_null(w.demotedDays) },
        counts: { unsure: unsure.length, demoted: demoted.length, sample: sample.length, sampledFrom: int_or_zero(c.sampledFrom) },
        sample, unsure, demoted,
    };
};
/** The tab's number: everything across the three lists; null while loading. */
export const review_total = (d) => (d ? d.counts.sample + d.counts.unsure + d.counts.demoted : null);

/** Heading + one-line explanation per section, read off the response
 *  (week, sample share, windows); sensible words when a field is missing. */
export const review_headings = (d) => {
    const r = d || normalize_client_review(null);
    const pct = pct_of(r.sampleRate);
    const share = pct === null ? 'A fixed sample' : `${pct}%`;
    const pool = r.counts.sampledFrom ? ` (${r.counts.sample} of ${r.counts.sampledFrom})` : '';
    return {
        sample: {
            title: `Weekly spot-check${r.isoWeek ? ` · week ${r.isoWeek}` : ''}`,
            blurb: `${share} of what went client-safe automatically ${days_text(r.windows.sampleDays)}${pool}, fixed for the week — a check that the automation reads right to clients.`,
        },
        unsure: {
            title: 'Safety screen unsure',
            blurb: 'The safety screen could not decide whether these are safe to show clients. Looks right makes a unit client-facing; Wrong or outdated keeps it internal.',
        },
        demoted: {
            title: `Demoted ${days_text(r.windows.demotedDays)}`,
            blurb: 'Units that came off the client tier, with why. Looks right puts one back as rep-attested; Wrong or outdated records that it should stay off.',
        },
    };
};

/** "3 clients · used in 12 suggestions · 0 flags · 4 helpful · 1 unhelpful" */
export const evidence_text = (e) => {
    const v = normalize_evidence(e);
    return [plural(v.clients, 'client'), `used in ${plural(v.uses, 'suggestion')}`, plural(v.flags, 'flag'), `${v.helpful} helpful`, `${v.unhelpfulUsers} unhelpful`].join(' · ');
};

/** The rep's two actions are the existing unit signals — the same routes the
 *  Chat thread's ✓ / ✗ use: attest confirms (→ REP_ATTESTED, client-facing),
 *  flag says wrong / outdated (an auto-attested unit is demoted that night). */
export const SIGNALS = {
    attest: { label: 'Looks right', title: 'Confirms this fact under your name — it becomes rep-attested and client-facing' },
    flag: { label: 'Wrong or outdated', title: 'Flags this fact as wrong or outdated, with a short reason — an auto-attested unit comes off the client tier tonight' },
};
export const signal_path = (id, kind) => `/api/v1/knowledge/units/${encodeURIComponent(id)}/${kind}`;
/** POST body: attest carries nothing here (no channel context); flag carries
 *  the reason, trimmed — the button does not send without one. */
export const signal_body = (kind, reason) => (kind === 'flag' ? { reason: String(reason || '').trim() } : {});
export const FLAG_REASON_MAX = 600;

const ATTEST_NOTES = { sample: 'stays client-facing as a rep-attested fact', unsure: 'now client-facing as a rep-attested fact', demoted: 'back in front of clients as a rep-attested fact' };
const FLAG_NOTES = { sample: 'comes off the client tier tonight', unsure: 'stays internal', demoted: 'stays off the client tier' };
/** The inline result on a card once a signal is sent: {text, note}. */
export const signal_receipt = (local, section) => {
    if (!local || !SIGNALS[local.kind]) return { text: '', note: '' };
    if (local.pending) return { text: local.kind === 'attest' ? '✓ confirming…' : '✗ flagging…', note: '' };
    const notes = local.kind === 'attest' ? ATTEST_NOTES : FLAG_NOTES;
    return {
        text: local.kind === 'attest' ? '✓ Looks right — confirmed' : `✗ Wrong or outdated${local.reason ? ` — ${local.reason}` : ''}`,
        note: [local.duplicate ? 'already recorded for you' : null, notes[section] || null].filter(Boolean).join(' · '),
    };
};
export const review_dom_id = (section, id) => `review-${section}-${id}`;

const REVIEW_INTRO = 'Client-facing knowledge now promotes itself: what the code confirms or two clients corroborate reaches reps, and a code-consistent unit that passes the safety screen reaches clients. These three short lists are the human check on that — not a queue to clear. Looks right confirms a unit under your name; Wrong or outdated flags it, with a reason.';
const REVIEW_FOOTER = 'Signals are recorded with your name. Looks right lifts a unit to rep-attested; Wrong or outdated records a flag — an auto-attested unit then comes off the client tier that night. Cards stay put until you refresh.';

/** One unit under review: what it says, its chips, the evidence line, why it
 *  is here (the screen's reasons · the demotion reason · the promotion
 *  confidence), then Looks right / Wrong or outdated. `local` is this
 *  session's signal for the unit ({pending} while the POST is in flight); the
 *  card stays in place either way, showing the result. */
function ReviewCard({ item, section, local, on_signal }) {
    const [flagging, set_flagging] = useState(false);
    const [reason, set_reason] = useState('');
    const u = item;
    const pending = !!(local && local.pending);
    const chip = consistency_chip(u);
    const corr = corroboration_chip(u.corroboration);
    const client_safe = u.audience === 'CLIENT_SAFE';
    const pct = u.confidence === null ? null : Math.round(u.confidence * 100);
    const promo = u.promotionConfidence === null ? null : Math.round(u.promotionConfidence * 100);
    const when = !u.at ? '' : section === 'sample' ? `client-safe since ${fmt_ts(u.at)}` : section === 'demoted' ? `demoted ${fmt_ts(u.at)}` : `screened ${fmt_ts(u.at)}`;
    const receipt = signal_receipt(local, section);
    const send_flag = () => {
        const r = reason.trim();
        if (!r) return;
        set_flagging(false);
        on_signal(u.id, 'flag', r);
    };
    return (
        <div id={review_dom_id(section, u.id)} style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={S.pill}>{u.module || 'OTHER'}</span>
                {u.feature ? <span style={{ fontSize: 12, color: '#5d6d7e' }}>{u.feature}</span> : null}
                {pct !== null ? <span title={`confidence ${pct}%`} style={{ width: 7, height: 7, borderRadius: 4, background: conf_color(u.confidence), flex: 'none' }} /> : null}
                {corr ? <span style={S.badge} title={corr.hint}>{corr.label}</span> : null}
                {u.status ? <span style={{ ...S.badge, color: '#5d6d7e', background: '#f1f4f7', borderColor: '#dde3e8' }} title='Unit status'>{u.status}</span> : null}
                <ConsistencyChip chip={chip} open={false} />
                {u.audience ? (
                    <span style={{ ...S.badge, color: client_safe ? '#2e9e5b' : '#8a6d1d', background: client_safe ? '#eefaf2' : '#fff8e6', borderColor: client_safe ? '#cdebd8' : '#f0d48a' }}
                        title={client_safe ? 'May be paraphrased to clients in reply suggestions' : 'Internal only — never surfaced to clients'}>
                        {client_safe ? 'client-safe' : 'internal'}
                    </span>
                ) : null}
                <span style={{ ...S.muted, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{when}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#233240', marginTop: 8 }}>{u.title || <i style={{ color: '#95a5a6' }}>(untitled)</i>}</div>
            <div style={{ ...S.body, marginTop: 4 }}>{u.body || <i style={{ color: '#95a5a6' }}>(no body)</i>}</div>
            {u.triggers.length ? <div style={{ fontSize: 11.5, color: '#7f8c8d', marginTop: 6 }}>Clients say: {u.triggers.join(', ')}</div> : null}
            <div style={{ fontSize: 11.5, color: '#5d6d7e', marginTop: 8 }}><span style={S.muted}>Evidence: </span>{evidence_text(u.evidence)}</div>
            {section === 'unsure' ? (
                <div style={{ ...S.why, borderLeft: '3px solid #f0d48a', padding: '2px 8px' }}>
                    <span style={{ color: '#8a6d1d', fontWeight: 600 }}>Screen unsure: </span>{u.reasons.length ? u.reasons.join(' · ') : 'no reasons recorded'}
                </div>
            ) : null}
            {section === 'demoted' ? (
                <div style={{ ...S.why, borderLeft: '3px solid #f2c9c4', padding: '2px 8px' }}>
                    <span style={{ color: '#c0392b', fontWeight: 600 }}>Why demoted: </span>{u.reason || 'no reason recorded'}
                </div>
            ) : null}
            {section === 'sample' ? (
                <div style={S.why}><span style={{ color: '#4a3f8f', fontWeight: 600 }}>Auto-attested</span>{u.via ? ` via ${u.via}` : ''}{promo !== null ? ` · confidence ${promo}%` : ''}</div>
            ) : null}
            {local ? (
                <div style={{ marginTop: 12, borderTop: '1px solid #eef2f5', paddingTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: local.kind === 'attest' ? '#2e9e5b' : '#c0392b' }}>{receipt.text}</span>
                    {receipt.note ? <span style={{ fontSize: 11.5, color: '#7f8c8d' }}>{receipt.note}</span> : null}
                    {pending ? <Spinner size='sm' style={{ width: 12, height: 12, flex: 'none' }} /> : null}
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                        <Button size='sm' color='success' outline data-signal='attest' title={SIGNALS.attest.title} onClick={() => on_signal(u.id, 'attest')} style={{ fontSize: 12, padding: '2px 12px' }}>
                            {SIGNALS.attest.label}
                        </Button>
                        {!flagging ? (
                            <Button size='sm' color='danger' outline data-signal='flag' title={SIGNALS.flag.title} onClick={() => set_flagging(true)} style={{ fontSize: 12, padding: '2px 12px' }}>
                                {SIGNALS.flag.label}
                            </Button>
                        ) : null}
                    </div>
                    {flagging ? (
                        <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                            <Input bsSize='sm' value={reason} placeholder='What is wrong or outdated? (a short reason is needed)' autoFocus maxLength={FLAG_REASON_MAX}
                                onChange={(e) => set_reason(e.target.value)}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') { e.preventDefault(); send_flag(); }
                                    else if (e.key === 'Escape') { e.preventDefault(); set_flagging(false); }
                                }}
                                style={{ fontSize: 11, padding: '2px 6px', height: 24 }} />
                            <Button size='sm' color='danger' outline data-signal='flag-send' disabled={!reason.trim()} style={{ fontSize: 11, padding: '0 8px' }} onClick={send_flag}>Flag</Button>
                            <Button size='sm' color='light' style={{ fontSize: 11, padding: '0 8px' }} onClick={() => set_flagging(false)}>Cancel</Button>
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

/** Review queue: the three lists from GET /knowledge/client-review, each with
 *  its heading and one-line explanation; an empty list says so quietly. No
 *  counts of what is left, no badges — the automation does the promoting. */
function ClientReview({ data, error, signals, on_signal }) {
    const headings = review_headings(data);
    return (
        <div style={{ maxWidth: 920 }}>
            <div style={{ ...S.card, fontSize: 12, color: '#5d6d7e' }}>{REVIEW_INTRO}</div>
            {error ? <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>Could not load the review queue — {cut_text(error, 200)}</div> : null}
            {data === null ? <div style={{ padding: 8 }}><Spinner size='sm' style={{ width: 12, height: 12 }} /></div> : null}
            {data !== null && !error ? REVIEW_SECTIONS.map((s) => {
                const rows = data[s.key];
                return (
                    <div key={s.key} data-section={s.key} style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#233240' }}>{headings[s.key].title}</div>
                        <div style={{ fontSize: 11.5, color: '#7f8c8d', marginBottom: 8 }}>{headings[s.key].blurb}</div>
                        {rows.length === 0
                            ? <div style={{ ...S.card, color: '#95a5a6', fontSize: 12.5, padding: '12px 16px' }}>{s.empty}</div>
                            : rows.map((u) => <ReviewCard key={u.id} item={u} section={s.key} local={signals[u.id] || null} on_signal={on_signal} />)}
                    </div>
                );
            }) : null}
            {review_total(data) ? <div style={{ fontSize: 10.5, color: '#b0b8bd', marginTop: 6 }}>{REVIEW_FOOTER}</div> : null}
        </div>
    );
}

/* ---------- Rep review (the CS reps' one short queue) ---------- */

/** One item of the rep review queue: what it is, the question it asks, what
 *  Right / Wrong mean for it (said plainly on the card), then one tap. A
 *  corrected text is optional (Wrong sends it; a contradiction's note goes with
 *  any answer). `local` is this session's answer ({pending} while in flight);
 *  the card stays in place showing the result. */
function RepReviewCard({ item, local, on_answer, now }) {
    const [writing, set_writing] = useState(false);
    const [text, set_text] = useState('');
    const k = REP_KINDS[item.kind];
    const u = item.unit;
    const c = item.case;
    const answered = local || (item.answer ? { verdict: item.answer.verdict, text: item.answer.text, pending: false } : null);
    const receipt = rep_receipt(answered, item.kind);
    const typed = text.trim();
    const send = (verdict) => on_answer(item, verdict, text_sent_with(item.kind, verdict) ? typed : '');
    const tone = receipt ? (receipt.tone === 'good' ? '#2e9e5b' : receipt.tone === 'bad' ? '#c0392b' : '#7f8c8d') : null;
    return (
        <div id={rep_dom_id(item.id)} data-kind={item.kind} style={S.card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={S.impact}>{k.label}</span>
                {u && u.module ? <span style={S.pill}>{u.module}</span> : c && c.module ? <span style={S.pill}>{c.module}</span> : null}
                {u && u.feature ? <span style={{ fontSize: 12, color: '#5d6d7e' }}>{u.feature}</span> : null}
                <span style={{ fontSize: 11.5, color: '#5d6d7e' }}>{demand_text(item.demand)}</span>
                {!answered ? <span style={{ ...S.muted, marginLeft: 'auto', whiteSpace: 'nowrap' }}>{expiry_text(item.expiresAt, now)}</span> : null}
            </div>
            <div style={{ fontSize: 12.5, color: '#233240', marginTop: 8, fontWeight: 600 }}>{k.question}</div>
            {item.kind === 'contradiction' && c ? (
                <>
                    <div style={{ ...S.body, marginTop: 6 }}><span style={S.muted}>{c.clientName ? `${c.clientName} asked: ` : 'The client asked: '}</span>{c.question}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <div style={S.column}><div style={S.column_label}>Knowledge base</div><div style={S.body}>{c.kbAnswer}</div></div>
                        <div style={S.column}>
                            <div style={S.column_label}>Rep</div><div style={S.body}>{c.repAnswer}</div>
                            {c.repQuote ? <div style={S.quote}>{c.repQuote}</div> : null}
                        </div>
                    </div>
                    {c.nature ? <div style={S.why}>Routed as: {c.nature.replace(/_/g, ' ')} — no product check can settle it</div> : null}
                </>
            ) : item.kind === 'served_correction' && u ? (
                <>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#233240', marginTop: 8 }}>{u.title}</div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                        <div style={S.column}><div style={S.column_label}>Clients were told</div><div style={S.body}>{item.before ? item.before.body : <i style={{ color: '#95a5a6' }}>(not on record)</i>}</div></div>
                        <div style={S.column}><div style={S.column_label}>Clients are told now</div><div style={S.body}>{u.body}</div></div>
                    </div>
                </>
            ) : u ? (
                <>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#233240', marginTop: 8 }}>{u.title || <i style={{ color: '#95a5a6' }}>(untitled)</i>}</div>
                    <div style={{ ...S.body, marginTop: 4 }}>{u.body || <i style={{ color: '#95a5a6' }}>(no body)</i>}</div>
                </>
            ) : <div style={{ ...S.muted, marginTop: 6 }}>(the fact is no longer available)</div>}
            {item.reasons.length ? <div style={S.why}><span style={{ color: '#5d6d7e', fontWeight: 600 }}>Why it is here: </span>{item.reasons.join(' · ')}</div> : null}
            <div data-meaning style={{ fontSize: 11.5, color: '#4a3f8f', marginTop: 8 }}>{k.right}. {k.wrong}.</div>
            {receipt ? (
                <div style={{ marginTop: 10, borderTop: '1px solid #eef2f5', paddingTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span data-receipt style={{ fontSize: 12.5, fontWeight: 600, color: tone }}>{receipt.text}</span>
                    {item.answer && item.answer.byName && !local ? <span style={S.muted}>by {item.answer.byName}</span> : null}
                    {answered.pending ? <Spinner size='sm' style={{ width: 12, height: 12, flex: 'none' }} /> : null}
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                        <Button size='sm' color='success' outline data-verdict='right' title={k.right} onClick={() => send('right')} style={{ fontSize: 12, padding: '2px 12px' }}>{REP_VERDICTS.right}</Button>
                        <Button size='sm' color='danger' outline data-verdict='wrong' title={k.wrong} onClick={() => send('wrong')} style={{ fontSize: 12, padding: '2px 12px' }}>{wrong_label(item.kind, text)}</Button>
                        <Button size='sm' color='light' data-verdict='unsure' title='Nothing changes — the same as not answering' onClick={() => send('unsure')} style={{ fontSize: 12, padding: '2px 12px', border: '1px solid #dde3e8' }}>{REP_VERDICTS.unsure}</Button>
                        {!writing ? (
                            <span data-action='write' onClick={() => set_writing(true)} style={{ fontSize: 11.5, color: '#2f6fed', cursor: 'pointer', marginLeft: 4 }}>
                                {item.kind === 'contradiction' ? '+ note' : '✎ write the correct text'}
                            </span>
                        ) : null}
                    </div>
                    {writing ? (
                        <div style={{ marginTop: 6 }}>
                            <div style={S.label}>{k.text}</div>
                            <Input type='textarea' rows={3} value={text} maxLength={REP_TEXT_MAX} autoFocus
                                placeholder={item.kind === 'contradiction' ? '' : (u && u.body) || ''}
                                onChange={(e) => set_text(e.target.value)}
                                onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Escape') { e.preventDefault(); set_writing(false); set_text(''); } }}
                                style={{ fontSize: 12.5 }} />
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

/** Rep review: the open items from GET /knowledge/rep-queue, in the server's
 *  ranking; kind a's FYI line while the contradictions are not routed. */
function RepReview({ data, error, answers, on_answer }) {
    const fyi = data && !error ? unrouted_text(data.unroutedContradictions) : null;
    const now = Date.now();
    return (
        <div style={{ maxWidth: 920 }}>
            <div style={{ ...S.card, fontSize: 12, color: '#5d6d7e' }}>{REP_INTRO}</div>
            {error ? <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>Could not load the rep review — {cut_text(error, 200)}</div> : null}
            {data === null ? <div style={{ padding: 8 }}><Spinner size='sm' style={{ width: 12, height: 12 }} /></div> : null}
            {data !== null && !error && data.items.length === 0 ? <div style={{ ...S.card, color: '#95a5a6', fontSize: 12.5, padding: '12px 16px' }}>{REP_EMPTY}</div> : null}
            {data !== null && !error ? data.items.map((it) => <RepReviewCard key={it.id} item={it} local={answers[it.id] && !answers[it.id].error ? answers[it.id] : null} on_answer={on_answer} now={now} />) : null}
            {fyi ? <div data-fyi style={{ fontSize: 11.5, color: '#7f8c8d', marginTop: 4, marginBottom: 6 }}>{fyi}</div> : null}
            {data && data.items.length ? <div style={{ fontSize: 10.5, color: '#b0b8bd', marginTop: 6 }}>{REP_FOOTER}</div> : null}
        </div>
    );
}

/* ---------- Gaps (what the client assistant could not answer) ---------- */

export const GAPS_INTRO = 'Questions the client assistant could not answer, grouped by meaning and ranked breadth first — a question several orgs ask leads, because that is a hole in the product\'s shared knowledge rather than one org\'s own workflow. Writing the answer here saves it as a rep-attested fact; a client-safe one grounds the assistant from then on.';
export const GAPS_FOOTER = 'Saved as a draft fact in your name; client-safe facts ground the assistant at once, and any rep can flag them.';

/** The status line's colours per code_suggestion_tone: text and the block's left rule. */
const CODE_TONES = {
    warn: { color: '#8a6d1d', border: '#f0d48a' },
    ok: { color: '#2e9e5b', border: '#bfe3cc' },
    info: { color: '#2f6fed', border: '#d9e6ff' },
    muted: { color: '#95a5a6', border: '#d6dbdf' },
};

/** What the nightly gaps step made of the cluster from the product code
 *  (docs/design-knowledge-miner.md §6.13), under the questions: one status
 *  line in the outcome's tone, the draft — its title, and the body folded
 *  past CODE_BODY_PREVIEW behind more / less — the gate's reason, and the
 *  cited files as monospace lines. The unverified status line already quotes
 *  the draft's title, so that outcome shows the body alone under it. Staff
 *  only — the CSP is. No suggestion → the card is as before. */
function CodeSuggestion({ cs }) {
    const [open, set_open] = useState(false);
    const tone = CODE_TONES[code_suggestion_tone(cs)] || CODE_TONES.muted;
    const body = code_suggestion_body(cs, open);
    const note = code_suggestion_note(cs);
    const paths = code_suggestion_paths(cs);
    const titled = !!cs.unit && cs.outcome !== 'unverified';
    return (
        <div data-role='gap-code' style={{ ...S.code, borderLeft: `3px solid ${tone.border}` }}>
            <div style={S.group}>
                <span style={S.group_label}>Code</span>
                <span data-role='gap-code-status' style={{ fontSize: 12, fontWeight: 600, color: tone.color }}>{code_suggestion_label(cs)}</span>
                {cs.derivedAt ? <span style={S.muted} title='When the nightly gaps step finished this attempt'>· {fmt_ts(cs.derivedAt)}</span> : null}
            </div>
            {cs.unit ? (
                <div style={{ marginTop: 4 }}>
                    {titled && cs.unit.title ? <div style={{ fontSize: 12.5, fontWeight: 600, color: '#33414e' }}>{cs.unit.title}</div> : null}
                    {body.text ? (
                        <div style={S.body}>
                            {body.text}
                            {body.collapsible ? (
                                <span data-action='code-more' onClick={() => set_open((v) => !v)} style={{ fontSize: 11, color: '#2f6fed', cursor: 'pointer', marginLeft: 6, whiteSpace: 'nowrap' }}>{open ? 'less' : 'more'}</span>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {note ? <div style={{ ...S.why, marginTop: 4, whiteSpace: 'pre-wrap' }}>{note}</div> : null}
            {paths.length ? (
                <div style={{ marginTop: 6 }}>
                    {paths.map((p) => <div key={p} data-role='gap-code-path' style={S.code_path} title={p}>{p}</div>)}
                </div>
            ) : null}
        </div>
    );
}

/** One cluster of unanswered questions. The question as first asked leads;
 *  under it how often, how many orgs (a name sample) and when; then the
 *  modules and portals it came from and "retrieved nothing N×" when retrieval
 *  itself came back empty — the strongest gap signal, shown in amber; the
 *  other phrasings collapsed; then the code's suggestion (CodeSuggestion) when
 *  the gaps step has one. "Write this answer" opens UnitFields seeded from
 *  the cluster — and from the code's draft when there is one
 *  (gap_form_seed_from_suggestion) — and saves with POST /knowledge/units. On
 *  success the card collapses to a receipt (the fact linked, like a citation);
 *  a refused save gives the form back with the rep's text and the api's answer. */
function GapCard({ gap, local, on_write, on_navigate }) {
    const [writing, set_writing] = useState(false);
    const [show_phrasings, set_show_phrasings] = useState(false);
    const [form, set_form] = useState(() => gap_form_seed_from_suggestion(gap, gap.codeSuggestion, KNOWLEDGE_MODULES));
    const location = useLocation();
    const g = gap;
    const cs = g.codeSuggestion || null;

    if (gap_answered(local)) {
        const unit = local.unit || null;
        return (
            <div id={gap_dom_id(g.clusterKey)} style={S.receipt}>
                <span style={{ color: '#2e9e5b', fontWeight: 600, whiteSpace: 'nowrap' }}>{gap_receipt_text(local)}</span>
                <span style={{ color: '#7f8c8d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {unit && unit.id ? (
                        <Link to={{ pathname: location.pathname, search: unit_search(unit.id) }} onClick={on_navigate} style={{ color: '#2f6fed' }} title='Open the saved fact'>
                            {cut_text(unit.title || g.question, 120)}
                        </Link>
                    ) : cut_text(g.question, 120)}
                </span>
                {local.pending ? <Spinner size='sm' style={{ width: 12, height: 12, flex: 'none' }} /> : null}
            </div>
        );
    }

    const upd = (k) => (e) => {
        const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        set_form((f) => ({ ...f, [k]: v }));
    };
    const invalid = gap_form_invalid(form);
    const save = () => {
        if (invalid) { window.alert('Title and the fact itself are required.'); return; }
        on_write(g.clusterKey, gap_unit_body(form, g.clusterKey));
    };
    const nothing = empty_retrievals_label(g.emptyRetrievals);
    const phrasings = phrasings_label(g.otherPhrasings.length, show_phrasings);

    return (
        <div id={gap_dom_id(g.clusterKey)} style={S.card}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#233240' }}>{g.question}</div>
            <div style={{ ...S.muted, marginTop: 3 }}>{gap_meta_line(g)}</div>
            {g.modules.length || g.portals.length || nothing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                    {g.modules.map((m) => <span key={'m:' + m} style={S.pill} title='The module the question was asked about'>{m}</span>)}
                    {g.portals.map((p) => <span key={'p:' + p} style={S.badge} title='The portal it was asked from'>{p}</span>)}
                    {nothing ? (
                        <span style={{ ...S.badge, color: '#8a6d1d', background: '#fff8e6', borderColor: '#f0d48a' }} title={EMPTY_RETRIEVALS_HINT}>{nothing}</span>
                    ) : null}
                </div>
            ) : null}
            {phrasings ? (
                <div style={{ marginTop: 6 }}>
                    <span onClick={() => set_show_phrasings((v) => !v)} data-action='phrasings' style={{ fontSize: 11.5, color: '#2f6fed', cursor: 'pointer' }}>{phrasings}</span>
                    {show_phrasings ? g.otherPhrasings.map((p, i) => (
                        <div key={i} style={{ fontSize: 11.5, color: '#5d6d7e', borderLeft: '3px solid #d6dbdf', padding: '2px 8px', background: '#f6f8f9', borderRadius: 4, marginTop: 4 }}>“{p}”</div>
                    )) : null}
                </div>
            ) : null}
            {cs ? <CodeSuggestion cs={cs} /> : null}

            {writing ? (
                <>
                    <UnitFields form={form} on_change={upd} />
                    {local && local.error ? <div style={{ fontSize: 12, color: '#c0392b', marginTop: 8 }}>Could not save — {cut_text(local.error, 240)}</div> : null}
                </>
            ) : null}

            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                {!writing ? (
                    <Button size='sm' color='primary' data-action='write' onClick={() => set_writing(true)} style={{ fontSize: 12, padding: '2px 12px' }}
                        title={(cs && cs.unit ? 'Write the fact that answers this — pre-filled from the code\'s draft; ' : 'Write the fact that answers this — ') + 'saved as a rep-attested unit the assistant can use'}>
                        Write this answer
                    </Button>
                ) : (
                    <>
                        <Button size='sm' color='primary' data-action='save' onClick={save} disabled={invalid} style={{ fontSize: 12, padding: '2px 12px' }}>Save knowledge</Button>
                        <Button size='sm' color='light' data-action='cancel' onClick={() => set_writing(false)} style={{ fontSize: 12, padding: '2px 12px', border: '1px solid #dde3e8' }}>Cancel</Button>
                        <span style={S.muted}>{GAPS_FOOTER}</span>
                    </>
                )}
            </div>
        </div>
    );
}

/** Gaps: the clusters from GET /knowledge/unanswered (loaded by the page so
 *  the tab carries its count), the window's counts as one line, and a
 *  client-side filter over question / phrasings / org names. Server order is
 *  kept — it is the ranking. An empty list says which kind of empty it is:
 *  nothing clustered yet, or nothing left unanswered. */
function Gaps({ data, error, filter, on_filter, written, on_write, on_navigate }) {
    const rows = data ? filter_gaps(data.clusters, filter) : [];
    const total = data ? data.clusters.length : 0;
    const filtering = String(filter || '').trim().length > 0;
    return (
        <div style={{ maxWidth: 920 }}>
            <div style={{ ...S.card, fontSize: 12, color: '#5d6d7e' }}>
                <div>{GAPS_INTRO}</div>
                {data !== null && !error ? <div data-role='gap-summary' style={{ marginTop: 6, color: '#33414e', fontWeight: 600 }}>{gap_summary_line(data.counts, data.windowDays)}</div> : null}
            </div>
            {error ? <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>Could not load the gaps — {cut_text(error, 200)}</div> : null}
            {data === null ? <div style={{ padding: 8 }}><Spinner size='sm' style={{ width: 12, height: 12 }} /></div> : null}
            {data !== null && !error && total === 0 ? (
                <div style={{ ...S.card, color: '#7f8c8d', fontSize: 13, textAlign: 'center', padding: '28px 16px' }}>{empty_gaps_text(data.counts, data.windowDays)}</div>
            ) : null}
            {total > 0 ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                    <Input bsSize='sm' value={filter} data-role='gap-filter' placeholder='Filter by question, phrasing or org…' onChange={(e) => on_filter(e.target.value)} style={{ fontSize: 12, maxWidth: 380 }} />
                    {filtering ? <span style={S.muted}>{rows.length} of {total}</span> : null}
                </div>
            ) : null}
            {total > 0 && rows.length === 0 ? (
                <div style={{ ...S.card, color: '#7f8c8d', fontSize: 13, textAlign: 'center', padding: '28px 16px' }}>Nothing matches that filter.</div>
            ) : null}
            {rows.map((g) => <GapCard key={g.clusterKey} gap={g} local={written[g.clusterKey] || null} on_write={on_write} on_navigate={on_navigate} />)}
        </div>
    );
}

const SUBTITLES = {
    rep: 'a few checks a day, the ones clients ask about most — one tap each',
    review: 'the human check on client-facing knowledge — a weekly sample, what the safety screen was unsure about, what was demoted',
    answers: 'where the knowledge base and a rep answered a client differently — decide who was right',
    contradictions: 'proposals the code judge rejected because the product code says otherwise — for the product handover',
    proposals: 'proposals from the conversation miner — approving is optional',
    gaps: 'questions the client assistant could not answer — write the answer and it knows it from then on',
};
export const PROPOSALS_NOTE = 'the automation promotes what the code confirms or two clients corroborate; approving here is optional';
export const TABS = ['rep', 'review', 'answers', 'contradictions', 'proposals', 'gaps'];
export const DEFAULT_TAB = 'rep';
/** A ?unit= deep link (an assistant citation) opens Proposals, where the cited fact is pinned. */
export const initial_tab = (search) => (focused_unit_id(search) ? 'proposals' : DEFAULT_TAB);

export default function Knowledge() {
    // ?unit=<id> deep link from an assistant citation in the Chat thread.
    const location = useLocation();
    const history = useHistory();
    const focused = focused_unit_id(location.search);
    const [tab, set_tab] = useState(() => initial_tab(location.search)); // 'rep' | 'review' | 'answers' | 'contradictions' | 'proposals' | 'gaps'
    const [sort, set_sort] = useState(DEFAULT_SORT);
    const [consistency, set_consistency] = useState(''); // '' = all
    const [module, set_module] = useState(''); // '' = all
    const [modules_seen, set_modules_seen] = useState([]); // union across loads, so a module filter keeps the other chips
    const [consistency_seen, set_consistency_seen] = useState(false); // the backend sends verdicts → show that filter
    const [data, set_data] = useState(null); // null = loading; {count, proposals} — every page loaded so far
    const [error, set_error] = useState(null);
    const [exhausted, set_exhausted] = useState(false); // the last page came back short → no "Show 50 more"
    const [loading_more, set_loading_more] = useState(false);
    const [reload_n, set_reload_n] = useState(0);
    const [decisions, set_decisions] = useState({}); // proposal id → {kind, pending, mergedInto, unit, reason}
    // Rep review — the default tab; loaded on mount and on ↻ (the first view of a day tops the queue up).
    const [rep_queue, set_rep_queue] = useState(null); // null = loading; normalize_rep_queue(...)
    const [rep_queue_error, set_rep_queue_error] = useState(null);
    const [rep_answers, set_rep_answers] = useState({}); // item id → {verdict, text, pending, effects} | {error}
    // Review queue — loaded on mount and on ↻.
    const [review, set_review] = useState(null); // null = loading; normalize_client_review(...)
    const [review_error, set_review_error] = useState(null);
    const [signals, set_signals] = useState({}); // unit id → {kind, pending, reason, duplicate}
    // Code contradictions — loaded on mount as well, so the tab carries its count.
    const [contradictions, set_contradictions] = useState(null); // null = loading; {count, proposals}
    const [contradictions_error, set_contradictions_error] = useState(null);
    // Answer contradictions — likewise.
    const [answer_status, set_answer_status] = useState(DEFAULT_ANSWER_STATUS);
    const [answers, set_answers] = useState(null); // null = loading; {items, counts}
    const [answers_error, set_answers_error] = useState(null);
    const [verdicts, set_verdicts] = useState({}); // caseId → {pending, kind, verdict, effects}
    // Gaps — what the client assistant could not answer; loaded on mount as well, so the tab carries its count.
    const [gaps, set_gaps] = useState(null); // null = loading; normalize_unanswered(...)
    const [gaps_error, set_gaps_error] = useState(null);
    const [gap_filter, set_gap_filter] = useState('');
    const [written, set_written] = useState({}); // clusterKey → {pending, existing, unit} | {error}
    const answers_key = JSON.stringify(cases_query({ status: answer_status }));
    // The effect keys on the serialized query: impact ↔ most corroborated share a page, so toggling them never refetches.
    const query_key = JSON.stringify(proposals_query({ sort, consistency, module }));
    const query_ref = useRef(query_key); // so a late "Show 50 more" page never lands on a different query
    query_ref.current = query_key;

    useEffect(() => {
        let alive = true;
        set_error(null);
        set_data(null);
        set_loading_more(false);
        cs_analysis_fetch({ path: '/api/v1/knowledge/proposals', query: JSON.parse(query_key) })
            .then((r) => {
                if (!alive) return;
                const d = normalize_proposals(r);
                set_data(d);
                set_exhausted(d.proposals.length < PAGE_LIMIT);
                set_decisions({});
                set_modules_seen((seen) => modules_in(d.proposals, seen));
                if (d.proposals.some((p) => p.consistency)) set_consistency_seen(true);
            })
            .catch((e) => { if (alive) { set_data({ count: 0, proposals: [] }); set_exhausted(true); set_error((e && e.message) || 'could not load proposals'); } });
        return () => { alive = false; };
    }, [reload_n, query_key]);

    // "Show 50 more": the next page of the same query, appended.
    const load_more = async () => {
        if (!data || loading_more) return;
        const key = query_key;
        set_loading_more(true);
        try {
            const r = await cs_analysis_fetch({ path: '/api/v1/knowledge/proposals', query: proposals_query({ sort, consistency, module, offset: data.proposals.length }) });
            if (query_ref.current !== key) return;
            const next = normalize_proposals(r);
            set_data((d) => (d ? merge_pages(d, next) : next));
            set_exhausted(next.proposals.length < PAGE_LIMIT);
            set_modules_seen((seen) => modules_in(next.proposals, seen));
            if (next.proposals.some((p) => p.consistency)) set_consistency_seen(true);
        } catch (e) {
            window.alert(`Could not load more — ${cut_text((e && e.message) || 'please try again', 200)}`);
        } finally {
            if (query_ref.current === key) set_loading_more(false);
        }
    };

    // Optimistic: collapse the card to a pending receipt at once, fill in the
    // server's verdict (mergedInto / unit) when it lands, roll back on error.
    const on_decide = async (id, action, body) => {
        const kind = action === 'approve' ? 'approved' : 'rejected';
        set_decisions((d) => ({ ...d, [id]: { kind, pending: true, reason: body && body.reason ? body.reason : null } }));
        try {
            const r = await cs_analysis_fetch({ path: `/api/v1/knowledge/units/${encodeURIComponent(id)}/${action}`, method: 'POST', body: body || {} });
            set_decisions((d) => ({
                ...d,
                [id]: kind === 'approved'
                    ? { kind, pending: false, mergedInto: (r && r.mergedInto) || null, unit: (r && r.unit) || null }
                    : { kind, pending: false, reason: body && body.reason ? body.reason : null },
            }));
        } catch (e) {
            set_decisions((d) => { const n = { ...d }; delete n[id]; return n; });
            window.alert(`Could not ${action} that — ${cut_text((e && e.message) || 'please try again', 200)}`);
        }
    };

    useEffect(() => {
        let alive = true;
        set_rep_queue_error(null);
        set_rep_queue(null);
        cs_analysis_fetch({ path: REP_QUEUE_PATH })
            .then((r) => { if (!alive) return; set_rep_queue(normalize_rep_queue(r)); set_rep_answers({}); })
            .catch((e) => { if (alive) { set_rep_queue(normalize_rep_queue(null)); set_rep_queue_error((e && e.message) || 'could not load the rep review'); } });
        return () => { alive = false; };
    }, [reload_n]);

    // One tap: pending at once, the result when it lands; the card stays in place. A changed answer's plain Wrong is
    // its undo. Rolled back on error (an item answered meanwhile by someone else says so).
    const on_rep_answer = async (item, verdict, text) => {
        const body = rep_answer_body(item.kind, verdict, text);
        const undo = item.kind === 'served_correction' && verdict === 'wrong' && !body.text;
        set_rep_answers((a) => ({ ...a, [item.id]: { verdict, text: body.text || null, pending: true } }));
        try {
            const r = await cs_analysis_fetch(undo ? { path: rep_undo_path(item.id), method: 'POST' } : { path: rep_answer_path(item.id), method: 'POST', body });
            const saved = normalize_rep_answer(r && r.item && r.item.answer);
            set_rep_answers((a) => ({ ...a, [item.id]: { verdict: saved ? saved.verdict : verdict, text: body.text || null, pending: false, effects: (r && r.effects) || null } }));
        } catch (e) {
            set_rep_answers((a) => { const n = { ...a }; delete n[item.id]; return n; });
            window.alert(`Could not record that — ${cut_text((e && e.message) || 'please try again', 200)}`);
        }
    };

    useEffect(() => {
        let alive = true;
        set_review_error(null);
        set_review(null);
        cs_analysis_fetch({ path: '/api/v1/knowledge/client-review' })
            .then((r) => { if (!alive) return; set_review(normalize_client_review(r)); set_signals({}); })
            .catch((e) => { if (alive) { set_review(normalize_client_review(null)); set_review_error((e && e.message) || 'could not load the review queue'); } });
        return () => { alive = false; };
    }, [reload_n]);

    // A signal: pending at once, the result when it lands; the card stays in
    // place either way. Rolled back on error.
    const on_signal = async (id, kind, reason) => {
        const body = signal_body(kind, reason);
        set_signals((s) => ({ ...s, [id]: { kind, pending: true, reason: body.reason || null } }));
        try {
            const r = await cs_analysis_fetch({ path: signal_path(id, kind), method: 'POST', body });
            set_signals((s) => ({ ...s, [id]: { kind, pending: false, reason: body.reason || null, duplicate: !!(r && r.duplicate) } }));
        } catch (e) {
            set_signals((s) => { const n = { ...s }; delete n[id]; return n; });
            window.alert(`Could not record that — ${cut_text((e && e.message) || 'please try again', 200)}`);
        }
    };

    useEffect(() => {
        let alive = true;
        set_contradictions_error(null);
        set_contradictions(null);
        cs_analysis_fetch({ path: '/api/v1/knowledge/contradictions' })
            .then((r) => { if (alive) set_contradictions(normalize_contradictions(r)); })
            .catch((e) => { if (alive) { set_contradictions({ count: 0, proposals: [] }); set_contradictions_error((e && e.message) || 'could not load contradictions'); } });
        return () => { alive = false; };
    }, [reload_n]);

    useEffect(() => {
        let alive = true;
        set_answers_error(null);
        set_answers(null);
        cs_analysis_fetch({ path: '/api/v1/knowledge/answer-contradictions', query: JSON.parse(answers_key) })
            .then((r) => { if (!alive) return; set_answers(normalize_cases(r)); set_verdicts({}); })
            .catch((e) => { if (alive) { set_answers({ items: [], counts: { open: 0, triaged: 0 } }); set_answers_error((e && e.message) || 'could not load answer contradictions'); } });
        return () => { alive = false; };
    }, [reload_n, answers_key]);

    // A verdict: pending at once, then the stored verdict + effects from the
    // server; the card stays in place either way. Rolled back on error.
    const on_verdict = async (case_id, kind, note) => {
        const body = verdict_body(kind, note);
        set_verdicts((v) => ({ ...v, [case_id]: { pending: true, kind } }));
        try {
            const r = await cs_analysis_fetch({ path: verdict_path(case_id), method: 'POST', body });
            set_verdicts((v) => ({
                ...v,
                [case_id]: {
                    pending: false, kind,
                    verdict: normalize_verdict(r && r.verdict) || { kind, note: body.note || null, byName: null, at: null },
                    effects: normalize_effects(r && r.effects),
                },
            }));
        } catch (e) {
            set_verdicts((v) => { const n = { ...v }; delete n[case_id]; return n; });
            window.alert(`Could not record that verdict — ${cut_text((e && e.message) || 'please try again', 200)}`);
        }
    };

    useEffect(() => {
        let alive = true;
        set_gaps_error(null);
        set_gaps(null);
        cs_analysis_fetch({ path: UNANSWERED_PATH })
            .then((r) => { if (!alive) return; set_gaps(normalize_unanswered(r)); set_written({}); })
            .catch((e) => { if (alive) { set_gaps(normalize_unanswered(null)); set_gaps_error((e && e.message) || 'could not load the gaps'); } });
        return () => { alive = false; };
    }, [reload_n]);

    // "Write this answer": the row collapses to a pending receipt at once and
    // fills in the server's verdict when it lands — a fresh unit, or the
    // existing fact with the same content that was confirmed instead. A
    // refused save gives the row back with the rep's text and the api's
    // answer; nothing typed is lost.
    const on_write = async (key, body) => {
        set_written((w) => ({ ...w, [key]: { pending: true } }));
        try {
            const r = await cs_analysis_fetch({ path: UNITS_PATH, method: 'POST', body });
            set_written((w) => ({ ...w, [key]: { pending: false, existing: !!(r && r.existing), unit: (r && r.unit) || null } }));
        } catch (e) {
            set_written((w) => ({ ...w, [key]: { error: (e && e.message) || 'please try again' } }));
        }
    };

    const proposals = data ? data.proposals : [];
    // The server already honours the filters; applying them again is a no-op there and covers an older backend.
    const visible = sort_proposals(filter_proposals(proposals, { consistency, module }), sort);
    const decided_n = proposals.filter((p) => decisions[p.id] && !decisions[p.id].pending).length;
    const remaining = data ? Math.max(0, data.count - decided_n) : null;
    const has_more = !!data && !error && !exhausted && data.count > proposals.length;
    const filtered = !!consistency || !!module;
    const modules = modules_in(proposals, modules_seen);
    const show_consistency_filter = consistency_seen || !!consistency;
    const proposals_tab = tab === 'proposals';
    // Open count net of the verdicts given on this page since the last load.
    const answer_items = answers ? answers.items : [];
    const decided_open_n = answer_items.filter((c) => !c.verdict && verdicts[c.caseId] && !verdicts[c.caseId].pending).length;
    const open_count = answers && !answers_error ? Math.max(0, answers.counts.open - decided_open_n) : null;
    const triaged_count = answers && !answers_error ? answers.counts.triaged + decided_open_n : null;
    const review_n = review && !review_error ? review_total(review) : null;
    const rep_n = rep_queue && !rep_queue_error ? rep_open_count(rep_queue, rep_answers) : null;
    const contradictions_n = contradictions && !contradictions_error ? contradictions.count : null;
    // Gap clusters in the window, net of the ones answered on this page since the last load.
    const answered_n = gaps ? gaps.clusters.filter((g) => gap_answered(written[g.clusterKey]) && !written[g.clusterKey].pending).length : 0;
    const gaps_n = gaps && !gaps_error ? Math.max(0, gaps_total(gaps) - answered_n) : null;

    return (
        <div style={{ padding: '22px 30px', background: '#f6f8fa', minHeight: 'calc(100vh - 60px)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <h4 style={{ margin: 0, color: '#233240' }}>Knowledge</h4>
                <span style={{ fontSize: 12, color: '#7f8c8d' }}>{SUBTITLES[tab]}</span>
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type='button' data-tab='rep' style={S.tab(tab === 'rep')} onClick={() => set_tab('rep')}
                        title='A few checks a day, ranked by how many clients ask — one tap each; skipped ones drop off after 7 days'>
                        Rep review{rep_n ? ` · ${rep_n}` : ''}
                    </button>
                    <button type='button' data-tab='review' style={S.tab(tab === 'review')} onClick={() => set_tab('review')}
                        title='The human check on client auto-promotion — a weekly sample, the units the safety screen was unsure about, and what was demoted'>
                        Review queue{review_n ? ` · ${review_n}` : ''}
                    </button>
                    <button type='button' data-tab='answers' style={S.tab(tab === 'answers')} onClick={() => set_tab('answers')}
                        title='Client questions the knowledge base answered differently from the rep — decide who was right'>
                        Answer contradictions{open_count ? ` · ${open_count}` : ''}
                    </button>
                    <button type='button' data-tab='contradictions' style={S.tab(tab === 'contradictions')} onClick={() => set_tab('contradictions')}
                        title='Proposals the code judge rejected because the product code says otherwise — for the product handover'>
                        Code contradictions{contradictions_n ? ` · ${contradictions_n}` : ''}
                    </button>
                    <button type='button' data-tab='proposals' style={S.tab(proposals_tab)} onClick={() => set_tab('proposals')}
                        title='What the conversation miner extracted — approving here is optional'>
                        Proposals
                    </button>
                    <button type='button' data-tab='gaps' style={S.tab(tab === 'gaps')} onClick={() => set_tab('gaps')}
                        title='Questions the client assistant could not answer, grouped by meaning and ranked by how many orgs ask — write the answer here'>
                        Gaps{gaps_n ? ` · ${gaps_n}` : ''}
                    </button>
                </span>
                {proposals_tab ? (data === null ? <Spinner size='sm' style={{ width: 12, height: 12 }} /> : (
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ ...S.badge, fontSize: 11.5, fontWeight: 600 }}>{remaining} {filtered ? 'matching ' : ''}proposal{remaining === 1 ? '' : 's'}</span>
                        <span style={{ fontSize: 11.5, color: '#7f8c8d' }}>· {PROPOSALS_NOTE}</span>
                    </span>
                )) : null}
                {tab === 'answers' ? (answers === null ? <Spinner size='sm' style={{ width: 12, height: 12 }} /> : (open_count !== null ? (
                    <span style={{ ...S.badge, fontSize: 11.5, fontWeight: 600 }}>{open_count} open · {triaged_count} triaged</span>
                ) : null)) : null}
                {(tab === 'rep' && rep_queue === null) || (tab === 'review' && review === null) || (tab === 'contradictions' && contradictions === null) || (tab === 'gaps' && gaps === null) ? <Spinner size='sm' style={{ width: 12, height: 12 }} /> : null}
                <span onClick={() => set_reload_n((n) => n + 1)} title='Refresh' style={{ cursor: 'pointer', color: '#95a5a6', fontSize: 15, lineHeight: 1, marginLeft: 'auto' }}>↻</span>
            </div>

            {tab === 'rep' ? (
                <RepReview data={rep_queue} error={rep_queue_error} answers={rep_answers} on_answer={on_rep_answer} />
            ) : tab === 'review' ? (
                <ClientReview data={review} error={review_error} signals={signals} on_signal={on_signal} />
            ) : tab === 'contradictions' ? (
                <Contradictions data={contradictions} error={contradictions_error} on_navigate={() => set_tab('proposals')}
                    on_review_in_queue={() => { set_consistency('CONTRADICTED'); set_tab('proposals'); }} />
            ) : tab === 'answers' ? (
                <AnswerContradictions data={answers} error={answers_error} status={answer_status} on_status={set_answer_status}
                    module={module} modules={modules_in(answer_items, module ? [module] : [])} on_module={set_module}
                    verdicts={verdicts} on_verdict={on_verdict} on_navigate={() => set_tab('proposals')} />
            ) : tab === 'gaps' ? (
                <Gaps data={gaps} error={gaps_error} filter={gap_filter} on_filter={set_gap_filter}
                    written={written} on_write={on_write} on_navigate={() => set_tab('proposals')} />
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                        <span style={S.group}>
                            <span style={S.group_label}>Sort</span>
                            {SORTS.map((s) => <button key={s.key} type='button' style={S.chip(sort === s.key)} onClick={() => set_sort(s.key)}>{s.label}</button>)}
                        </span>
                        {show_consistency_filter ? (
                            <span style={S.group}>
                                <span style={S.group_label}>Code check</span>
                                <button type='button' style={S.chip(!consistency)} onClick={() => set_consistency('')}>All</button>
                                {CONSISTENCY_KEYS.map((k) => (
                                    <button key={k} type='button' style={S.chip(consistency === k)} onClick={() => set_consistency(consistency === k ? '' : k)} title={CONSISTENCY[k].hint}>{CONSISTENCY[k].label}</button>
                                ))}
                            </span>
                        ) : null}
                        {modules.length > 1 || module ? (
                            <span style={S.group}>
                                <span style={S.group_label}>Module</span>
                                <button type='button' style={S.chip(!module)} onClick={() => set_module('')}>All</button>
                                {modules.map((m) => <button key={m} type='button' style={S.chip(module === m)} onClick={() => set_module(module === m ? '' : m)}>{m}</button>)}
                            </span>
                        ) : null}
                    </div>
                    {error ? <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 10 }}>Could not load proposals — {cut_text(error, 200)}</div> : null}
                    {data !== null && !error && visible.length === 0 ? (
                        <div style={{ ...S.card, color: '#7f8c8d', fontSize: 13, textAlign: 'center', padding: '28px 16px' }}>
                            {filtered ? 'Nothing matches these filters.' : 'No open proposals — the miner runs nightly.'}
                        </div>
                    ) : null}
                    <div style={{ maxWidth: 920 }}>
                        {focused ? (
                            <FocusedUnit unit_id={focused} in_queue={visible.some((p) => p.id === focused)}
                                on_clear={() => history.replace(location.pathname)} />
                        ) : null}
                        {visible.map((p) => <ProposalCard key={p.id} proposal={p} decision={decisions[p.id] || null} on_decide={on_decide} />)}
                        {has_more ? (
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4, marginBottom: 8 }}>
                                <Button size='sm' color='light' data-action='show-more' disabled={loading_more} onClick={load_more} style={{ fontSize: 12, padding: '2px 12px', border: '1px solid #dde3e8' }}>
                                    {loading_more ? 'Loading…' : `Show ${PAGE_LIMIT} more`}
                                </Button>
                                <span style={S.muted}>{proposals.length} of {data.count} loaded</span>
                            </div>
                        ) : null}
                    </div>
                    {visible.length ? (
                        <div style={{ fontSize: 10.5, color: '#b0b8bd', marginTop: 6 }}>
                            Approved client-safe facts ground AI reply suggestions at once; any rep can still ✓ / ✗ them from a suggestion. Rejected proposals are retired.
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}
